import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import * as fmt from '../i18n/format';
import { getCurrentCopy } from '../i18n/current';
import { fill } from '../i18n';
import { useAuth } from './AuthContext';
import { projectService } from '../services/projectService';
import { taskService, buildTaskTree, flattenTaskTree, parseDateFromDB } from '../services/taskService';
import { teamService } from '../services/teamService';
import { raciService } from '../services/raciService';
import { taskAssignmentService } from '../services/taskAssignmentService';
import { capacityService } from '../services/capacityService';
import {
  updateTaskInTree,
  deleteTaskFromTree,
  addTaskToTree,
  addTaskAfterTask,
  toggleTaskExpansion,
  duplicateTask,
  flattenTasks,
  moveTaskUp,
  moveTaskDown,
  indentTask,
  outdentTask,
  moveTaskToTarget
} from '../utils/taskUtils';
import { cloneTaskTree, diffTaskTrees, normalizePositions } from '../utils/taskSnapshot';
import { DEFAULT_COLUMNS, createColumn } from '../config/columnTypes';
import analytics from '../services/analyticsService';
import {
  getProjectDateSettings,
  processTaskUpdate,
  recalculateAllTasks,
  ensureWorkDay,
  propagateToParents,
  cleanInvalidPredecessors,
  isAncestorOf
} from '../utils/taskBusinessRules';
import type { TaskDateUpdates, CascadeUpdate } from '../utils/taskBusinessRules';
import { getNextWorkDay, addDays } from '../utils/dateUtils';
import { parseAppPath } from '../lib/appRoute';
import { useSubscription } from './SubscriptionContext';
import { useUIState } from './UIStateContext';
import { useUndoRedoContext } from './UndoRedoContext';
import { sprintService } from '../services/sprintService';
import { kanbanColumnService } from '../services/kanbanColumnService';
import {
  DEFAULT_KANBAN_COLUMNS,
  categoryOf,
  firstColumnOfCategory,
  progressForCategory,
} from '../config/kanbanCategories';

import type { Project, ProjectWithRole, Task, TaskRow, TeamMember, ColumnConfig, ProjectSettings, GlobalProjectStats, TaskUpdateInput, Sprint, KanbanColumn, ColumnCategory } from '../types';
import type { ProjectContextValue } from '../types';

const ProjectContext = createContext<ProjectContextValue | null>(null);

export { ProjectContext };

// Descrição amigável da ação de undo a partir dos campos alterados.
const describeUpdate = (updates: Record<string, unknown>): string => {
  const t = getCurrentCopy().common.undo;
  const keys = Object.keys(updates);
  if (keys.some(k => k === 'startDate' || k === 'endDate' || k === 'start_date' || k === 'end_date')) return t.dates;
  if (keys.includes('name')) return t.rename;
  if (keys.includes('progress')) return t.progress;
  if (keys.includes('status')) return t.status;
  if (keys.includes('assigned')) return t.assigned;
  if (keys.includes('dependencies')) return t.dependencies;
  if (keys.includes('color')) return t.color;
  return t.generic;
};

export const useProject = (): ProjectContextValue => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }: { children: React.ReactNode }) => {
  // Obter usuário autenticado e estado de logout
  const { user, isLoggingOut } = useAuth();
  const { canCreateProject: checkCanCreateProject, isSystemAdmin, refreshUsage } = useSubscription();
  const { currentModule } = useUIState();

  // Multi-project state
  const [projects, setProjects] = useState<ProjectWithRole[]>([]);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [taskAssignmentsMap, setTaskAssignmentsMap] = useState<Record<string, string[]>>({});

  /**
   * As colunas do quadro do projeto corrente (`kanban_columns`).
   *
   * Vive aqui, e não no KanbanModule, porque quem precisa delas não é só o
   * quadro: a barra do Gantt, a célula de status da tabela, o bloco do
   * Calendário e o Cronograma resolvem cor e rótulo do cartão pela mesma
   * lista. Duas leituras dessa lista dariam duas verdades.
   *
   * Nasce com as cinco canônicas e cai nelas sempre que a consulta vem vazia:
   * é o que deixa o front subir com segurança ANTES ou DEPOIS de a migration
   * rodar em produção, já que os dois gestos são manuais e nunca atômicos.
   */
  const [kanbanColumns, setKanbanColumns] = useState<KanbanColumn[]>(DEFAULT_KANBAN_COLUMNS);

  /**
   * Espelho das colunas para leitura DENTRO de `updateTask`.
   *
   * `updateTask` decide progresso pela categoria da coluna, e roda dentro de
   * um `setTasks(prev => ...)`. Ler o estado ali capturaria o valor do render
   * em que o callback foi criado; a ref lê sempre o valor corrente sem
   * arrastar a lista para as dependências do callback.
   */
  const kanbanColumnsRef = useRef<KanbanColumn[]>(DEFAULT_KANBAN_COLUMNS);
  useEffect(() => { kanbanColumnsRef.current = kanbanColumns; }, [kanbanColumns]);

  // Column management state
  const [columns, setColumns] = useState<ColumnConfig[]>([...DEFAULT_COLUMNS]);

  // UI state
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]); // Multi-select
  /** Tarefa a destacar assim que ela terminar de carregar. Ver focusTask(). */
  const [pendingFocusTaskId, setPendingFocusTaskId] = useState<string | null>(null);

  // Loading state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isLoadingRef = useRef(false); // Proteção contra chamadas duplicadas

  /**
   * O endereço com que a aba ABRIU, congelado no primeiro render.
   *
   * `loadProjects` só decide o projeto inicial depois da rede responder, e a
   * essa altura `window.location` já não é o link que a pessoa clicou: o app
   * normaliza a própria barra de endereço enquanto isso (useAppRouteSync). Ler
   * a URL lá dentro era ler um endereço que o próprio app tinha reescrito, e o
   * link de tarefa acabava abrindo o projeto errado.
   *
   * Ref, e não estado: é a intenção de entrada, não muda e não re-renderiza.
   */
  const rotaDeEntradaRef = useRef(parseAppPath(window.location.pathname));

  // ─── Undo/Redo (baseado em snapshot) ────────────────────────────────────────
  const { pushAction } = useUndoRedoContext();

  // Espelho do estado de tarefas em ref, para capturar snapshots sem depender de
  // closures stale dentro dos handlers (que usam setTasks(prev => ...)).
  const tasksRef = useRef<Task[]>(tasks);
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  // Quando setado, agrupa várias mutações numa única ação de undo (drag, ações em
  // lote). Enquanto ativo, os pushes individuais ficam suprimidos.
  const undoBatchRef = useRef<Task[] | null>(null);

  // Reconcilia o banco para igualar a árvore-alvo e aplica o alvo na UI.
  const restoreTree = useCallback(async (target: Task[]): Promise<void> => {
    const current = tasksRef.current;
    const diff = diffTaskTrees(target, current);

    // UI primeiro (posições normalizadas para coerência em memória)
    const normalized = normalizePositions(target);
    tasksRef.current = normalized;
    setTasks(normalized);

    // Recriar antes de atualizar (filhos re-apontam para pais recriados);
    // excluir por último.
    for (const r of diff.toRestore) {
      try {
        await taskService.restore({ ...(r.task as unknown as Record<string, unknown>), parent_id: r.parentId, position: r.position });
      } catch (err) { console.error('[undo] restore falhou:', err); }
    }
    for (const u of diff.toUpdate) {
      try {
        await taskService.update(u.id, u.payload as TaskUpdateInput);
      } catch (err) { console.error('[undo] update falhou:', err); }
    }
    for (const id of diff.toDelete) {
      try {
        await taskService.delete(id);
      } catch (err) { console.error('[undo] delete falhou:', err); }
    }
  }, []);

  // Empilha uma ação que restaura `before`. O estado pós-operação (para o redo) é
  // capturado preguiçosamente no momento do undo.
  const pushTreeUndo = useCallback((description: string, before: Task[]): void => {
    let after: Task[] | null = null;
    pushAction({
      type: 'tree-snapshot',
      description,
      undo: async () => {
        after = cloneTaskTree(tasksRef.current);
        await restoreTree(before);
      },
      redo: async () => {
        if (after) await restoreTree(after);
      }
    });
  }, [pushAction, restoreTree]);

  // Captura o estado atual; retorna null se já houver batch ativo.
  const captureBefore = useCallback((): Task[] | null => {
    if (undoBatchRef.current) return null;
    return cloneTaskTree(tasksRef.current);
  }, []);

  // Push individual, suprimido quando há batch ativo (o commit fará o push).
  const maybePushUndo = useCallback((description: string, before: Task[] | null): void => {
    if (!before || undoBatchRef.current) return;
    pushTreeUndo(description, before);
  }, [pushTreeUndo]);

  // Agrupa mutações subsequentes numa única ação (drag e ações em lote).
  const beginUndoCapture = useCallback((): void => {
    if (!undoBatchRef.current) undoBatchRef.current = cloneTaskTree(tasksRef.current);
  }, []);

  const commitUndoCapture = useCallback((description: string): void => {
    const before = undoBatchRef.current;
    undoBatchRef.current = null;
    if (!before) return;
    // Não empilhar entrada vazia (ex: clicou na barra do Gantt sem arrastar).
    // Seguro comparar aqui: o commit acontece no mouseup, após os setTasks do drag.
    const diff = diffTaskTrees(before, tasksRef.current);
    if (diff.toUpdate.length === 0 && diff.toRestore.length === 0 && diff.toDelete.length === 0) return;
    pushTreeUndo(description, before);
  }, [pushTreeUndo]);
  // ────────────────────────────────────────────────────────────────────────────

  // Load all projects when user is authenticated
  useEffect(() => {
    console.log('[ProjectContext] useEffect triggered, user:', user?.id, 'isLoggingOut:', isLoggingOut);

    // Não carregar se estiver fazendo logout
    if (isLoggingOut) {
      console.log('[ProjectContext] Logout em andamento, ignorando');
      return;
    }

    if (user?.id) {
      loadProjects(user.id);
    } else {
      // Limpar dados quando não há usuário
      console.log('[ProjectContext] No user, clearing data');
      setProjects([]);
      setCurrentProjectId(null);
      setProject(null);
      setTasks([]);
      setTeam([]);
      setLoading(false);
    }
  }, [user?.id, isLoggingOut]);

  // Load project data when currentProjectId changes
  useEffect(() => {
    if (currentProjectId) {
      loadProjectData(currentProjectId);
    }
  }, [currentProjectId]);

  // Recarregar projetos com stats ao voltar para Home (só se não tem dados)
  useEffect(() => {
    if (currentModule === 'home' && user?.id && !isLoggingOut && projects.length === 0) {
      loadProjects(user.id);
    }
  }, [currentModule, user?.id, isLoggingOut, projects.length]);

  const loadProjects = async (userId: string): Promise<void> => {
    console.log('[ProjectContext] loadProjects called with userId:', userId);

    // Proteção contra chamadas duplicadas
    if (!userId) {
      console.log('[ProjectContext] No userId, returning');
      setLoading(false);
      return;
    }

    if (isLoadingRef.current) {
      console.log('[ProjectContext] Já está carregando, ignorando chamada duplicada');
      return;
    }

    isLoadingRef.current = true;

    try {
      setLoading(true);
      setError(null);
      console.log('[ProjectContext] Calling getUserProjectsWithStats...');

      const data = await projectService.getUserProjectsWithStats(userId);
      console.log('[ProjectContext] Got data:', data);

      setProjects(data || []);

      // Projeto inicial: o da URL quando o usuario tem acesso a ele, senao o
      // primeiro da lista.
      //
      // O id nunca foi persistido, entao ate aqui todo reload caia no primeiro
      // projeto, independente de onde a pessoa estava. Agora que o endereco
      // carrega o projeto (/p/<id>/<modulo>), recarregar e abrir link profundo
      // voltam para o lugar certo. A checagem contra a lista carregada e o que
      // impede um link velho, ou de outra conta, de mandar o app buscar projeto
      // que devolve erro de tela cheia.
      if (data && data.length > 0 && !currentProjectId) {
        const fromUrl = rotaDeEntradaRef.current.projectId;
        const wanted = fromUrl && data.some(p => p.id === fromUrl) ? fromUrl : data[0].id;
        setCurrentProjectId(wanted);
      }

      console.log('[ProjectContext] Setting loading to false');
    } catch (err: unknown) {
      // Ignorar AbortError - causado pelo React StrictMode (não é erro real)
      const e = err as Error;
      if (e?.message?.includes('AbortError') || e?.name === 'AbortError') {
        console.log('[ProjectContext] AbortError ignorado (React StrictMode)');
        // NÃO fazer return - deixar o finally executar!
      } else {
        console.error('[ProjectContext] Erro ao carregar projetos:', err);
        // Mostrar mensagem amigável
        const tp = getCurrentCopy().common.project;
        const errorMessage = e.message?.includes('Timeout')
          ? tp.loadTimeout
          : fill(tp.loadError, { message: e.message || tp.unknownError });
        setError(errorMessage);
        setProjects([]);
      }
    } finally {
      // SEMPRE executar, mesmo após AbortError
      isLoadingRef.current = false;
      setLoading(false);
    }
  };

  const loadProjectData = async (projectId: string): Promise<void> => {
    if (!projectId) return;

    try {
      setLoading(true);

      // Load project, tasks, team, sprints, kanban columns and assignments in parallel
      const [projectData, tasksData, teamData, sprintsData, kanbanColumnsData, assignmentsData] = await Promise.all([
        projectService.getById(projectId),
        taskService.getByProject(projectId),
        teamService.getByProject(projectId),
        sprintService.getByProject(projectId),
        // Quadro é acessório da tela: se a consulta falhar (migration ainda não
        // aplicada em produção, RLS, rede), o Kanban abre com as cinco colunas
        // canônicas em vez de derrubar o projeto inteiro.
        kanbanColumnService.listByProject(projectId).catch(() => [] as KanbanColumn[]),
        taskAssignmentService.getByProject(projectId).catch(() => [])
      ]);

      // Build assignments map: taskId → [memberId1, memberId2, ...]
      const assignMap: Record<string, string[]> = {};
      for (const a of assignmentsData) {
        if (!assignMap[a.task_id]) assignMap[a.task_id] = [];
        // Primary first
        if (a.is_primary) {
          assignMap[a.task_id].unshift(a.team_member_id);
        } else {
          assignMap[a.task_id].push(a.team_member_id);
        }
      }

      setProject(projectData);
      setTasks(buildTaskTree(tasksData));
      setTeam(teamData);
      setSprints(sprintsData);
      // Lista vazia = projeto anterior ao backfill, ou front rodando antes da
      // migration. As canônicas mantêm a tela idêntica à de hoje.
      setKanbanColumns(kanbanColumnsData.length > 0 ? kanbanColumnsData : DEFAULT_KANBAN_COLUMNS);
      setTaskAssignmentsMap(assignMap);
      setSelectedTaskId(null);

      // Load columns from project settings or use defaults
      const savedColumns = (projectData as Project)?.settings?.columns;
      if (savedColumns && savedColumns.length > 0) {
        // Mapa de DEFAULT_COLUMNS para restaurar props que não são persistidas (system, editable, etc.)
        const defaultMap = new Map(DEFAULT_COLUMNS.map((dc) => [dc.id, dc]));

        // Restaurar propriedades de sistema nas colunas salvas
        const restoredColumns: ColumnConfig[] = savedColumns.map((sc: ColumnConfig) => {
          const dc = defaultMap.get(sc.id);
          if (dc) {
            // Manter width/visible/position do salvo, restaurar system/editable/deletable/type do default
            // Para colunas novas adicionadas como visible:true no DEFAULT mas salvas como false, usar o default
            const visible = (dc.visible && !sc.visible && sc.id === 'description') ? dc.visible : sc.visible;
            return { ...dc, width: sc.width, visible, position: sc.position };
          }
          return sc;
        });

        // Adicionar colunas de sistema novas que não existem nas salvas
        const savedIds = new Set(savedColumns.map((c: ColumnConfig) => c.id));
        const missingColumns = DEFAULT_COLUMNS.filter((dc) => dc.system && !savedIds.has(dc.id));
        if (missingColumns.length > 0) {
          const maxPos = Math.max(...restoredColumns.map((c) => c.position ?? 0));
          missingColumns.forEach((mc, idx) => {
            // A coluna de conversa estreia visível e na posição do default
            // (1.5, entre nome e descrição): ela é porta de entrada da
            // colaboração e escondida ninguém a descobriria. As demais novas
            // continuam entrando ocultas no fim, como sempre.
            if (mc.id === 'conversation') {
              restoredColumns.push({ ...mc });
            } else {
              restoredColumns.push({ ...mc, position: maxPos + 1 + idx, visible: false });
            }
          });
        }

        setColumns(restoredColumns);
      } else {
        setColumns([...DEFAULT_COLUMNS]);
      }
    } catch (err: unknown) {
      // Ignorar AbortError - causado pelo React StrictMode
      const e = err as Error;
      if (e?.message?.includes('AbortError') || e?.name === 'AbortError') {
        console.log('[ProjectContext] loadProjectData AbortError ignorado');
        // NÃO fazer return - deixar o finally executar!
      } else {
        console.error('Erro ao carregar dados do projeto:', err);
        setError(e.message);
      }
    } finally {
      // SEMPRE executar, mesmo após AbortError
      setLoading(false);
    }
  };

  // Project operations
  const createProject = async (name: string, description: string = ''): Promise<ProjectWithRole | null> => {
    // Verificar limite de projetos do plano
    if (!isSystemAdmin && !checkCanCreateProject()) {
      throw new Error('PLAN_LIMIT_REACHED');
    }

    try {
      setSaving(true);
      const newProject = await projectService.create({ name, description }, user!.id);

      // Verificar se o projeto foi criado (pode ser null se AbortError foi ignorado)
      if (!newProject) {
        console.log('[ProjectContext] createProject: newProject é null, recarregando página');
        // StrictMode aborta todas as requisições - recarregar página é a solução mais confiável
        window.location.reload();
        return null;
      }

      setProjects(prev => [newProject as ProjectWithRole, ...prev]);
      setCurrentProjectId(newProject.id);
      setSaving(false);
      analytics.trackProjectCreated(name);
      // Atualizar contagem de projetos no SubscriptionContext
      if (refreshUsage) refreshUsage();
      return newProject as ProjectWithRole;
    } catch (err: unknown) {
      // Ignorar AbortError - causado pelo React StrictMode (não é erro real)
      const e = err as Error;
      if (e?.message?.includes('AbortError') || e?.name === 'AbortError') {
        console.log('[ProjectContext] createProject AbortError ignorado, recarregando página');
        // StrictMode aborta todas as requisições - recarregar página é a solução mais confiável
        window.location.reload();
        return null;
      }
      console.error('Erro ao criar projeto:', err);
      setError(e.message);
      setSaving(false);
      throw err;
    }
  };

  const switchProject = (projectId: string): void => {
    if (projectId !== currentProjectId) {
      setCurrentProjectId(projectId);
    }
  };

  // Registra um projeto já criado fora do contexto (ex.: seed por IA/modelo) direto
  // no estado, sem re-consultar getUserProjectsWithStats. Espelha o que createProject
  // faz no caminho de projeto em branco: evita o ciclo de carga dupla e impede que o
  // spinner de tela cheia (loading && projects.length === 0) apareça no onboarding.
  const registerCreatedProject = (newProject: ProjectWithRole): void => {
    setProjects(prev => {
      // Evita duplicar se já estiver na lista (re-render/StrictMode).
      if (prev.some(p => p.id === newProject.id)) return prev;
      return [newProject, ...prev];
    });
    setCurrentProjectId(newProject.id);
    if (refreshUsage) refreshUsage();
  };

  /**
   * Aplica no estado um projeto que o service JÁ devolveu atualizado, sem rede.
   *
   * Existe para quem grava uma mudança pequena no projeto e precisa só refletir
   * o resultado. A alternativa que estava em uso, `loadProject`, custa caro
   * demais para isso: liga o `loading` global (duas rodadas de re-render em
   * TODO consumidor de `useProject`), dispara seis consultas em paralelo,
   * reconstrói a árvore de tarefas inteira e ainda limpa `selectedTaskId`.
   *
   * O sintoma que isso produzia: ao salvar no modal de publicação, o Gantt
   * inteiro era repintado atrás do overlay do diálogo e o fundador via a janela
   * "fechar e abrir de novo". O modal nunca desmontava; era o app piscando por
   * baixo dele, e a seleção de tarefa se perdia junto.
   *
   * Mesmo padrão de `renameProject`: remenda a lista e o projeto corrente.
   */
  const aplicarProjetoAtualizado = (projeto: Project): void => {
    if (!projeto?.id) return;
    setProjects(prev => prev.map(p => (p.id === projeto.id ? { ...p, ...projeto } : p)));
    if (currentProjectId === projeto.id) {
      setProject(prev => (prev ? { ...prev, ...projeto } : projeto));
    }
  };

  const deleteProject = async (projectId: string): Promise<void> => {
    try {
      setSaving(true);
      await projectService.delete(projectId);
      analytics.trackProjectDeleted();
      setProjects(prev => prev.filter(p => p.id !== projectId));

      // Switch to another project if current was deleted
      if (currentProjectId === projectId) {
        const remaining = projects.filter(p => p.id !== projectId);
        setCurrentProjectId(remaining.length > 0 ? remaining[0].id : null);
        if (remaining.length === 0) {
          setProject(null);
          setTasks([]);
          setTeam([]);
        }
      }
      setSaving(false);
    } catch (err: unknown) {
      // Ignorar AbortError - causado pelo React StrictMode
      const e = err as Error;
      if (e?.message?.includes('AbortError') || e?.name === 'AbortError') {
        console.log('[ProjectContext] deleteProject AbortError ignorado');
        setSaving(false);
        return;
      }
      console.error('Erro ao deletar projeto:', err);
      setError(e.message);
      setSaving(false);
      throw err;
    }
  };

  const renameProject = async (projectId: string, newName: string): Promise<void> => {
    try {
      setSaving(true);
      const updated = await projectService.update(projectId, { name: newName });
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...updated } : p));
      if (currentProjectId === projectId) {
        setProject(updated);
      }
      setSaving(false);
    } catch (err: unknown) {
      // Ignorar AbortError - causado pelo React StrictMode
      const e = err as Error;
      if (e?.message?.includes('AbortError') || e?.name === 'AbortError') {
        console.log('[ProjectContext] renameProject AbortError ignorado');
        setSaving(false);
        return;
      }
      console.error('Erro ao renomear projeto:', err);
      setError(e.message);
      setSaving(false);
      throw err;
    }
  };

  const duplicateProject = async (projectId: string): Promise<ProjectWithRole | null> => {
    // Verificar limite de projetos do plano
    if (!isSystemAdmin && !checkCanCreateProject()) {
      throw new Error('PLAN_LIMIT_REACHED');
    }

    try {
      setSaving(true);

      // Buscar o projeto original
      const originalProject = projects.find(p => p.id === projectId);
      if (!originalProject) {
        throw new Error(getCurrentCopy().common.project.notFound);
      }

      // Criar novo projeto com nome "Cópia de [nome original]"
      const newProject = await projectService.create({
        name: fill(getCurrentCopy().common.project.copyOf, { name: originalProject.name }),
        description: originalProject.description || '',
        settings: originalProject.settings || {}
      }, user!.id);

      // Buscar todas as tarefas do projeto original
      const originalTasks = await taskService.getByProject(projectId);

      // Copiar team_members — necessário para que tasks assigned e allocations funcionem
      const originalTeam = await teamService.getByProject(projectId).catch(() => [] as TeamMember[]);

      // Criar o projeto já produz o cartão do dono, pelo trigger
      // trg_espelhar_membro_como_pessoa. Copiar o cartão dele do original por
      // cima daria duas linhas da mesma pessoa no projeto novo, com as tarefas
      // apontando para a segunda. Reaproveita o que já existe, por vínculo.
      const jaNoProjetoNovo = await teamService.getByProject(newProject!.id).catch(() => [] as TeamMember[]);
      const cartaoPorUsuario = new Map(
        jaNoProjetoNovo.filter(m => m.linked_user_id).map(m => [m.linked_user_id!, m.id])
      );

      const teamIdMapping: Record<string, string> = {};
      for (const member of originalTeam) {
        const jaExiste = member.linked_user_id ? cartaoPorUsuario.get(member.linked_user_id) : undefined;
        if (jaExiste) {
          teamIdMapping[member.id] = jaExiste;
          continue;
        }
        try {
          const created = await teamService.create({
            project_id: newProject!.id,
            name: member.name,
            email: member.email,
            role_title: member.role_title,
            avatar_color: member.avatar_color,
            linked_user_id: member.linked_user_id,
            resource_id: member.resource_id,
          } as Partial<TeamMember> as TeamMember);
          if (created?.id) teamIdMapping[member.id] = created.id;
        } catch (copyErr) {
          console.warn('[ProjectContext.duplicateProject] Falha ao copiar membro:', member.name, copyErr);
        }
      }

      /**
       * O QUADRO é clonado antes das tarefas, com os MESMOS ids de coluna.
       *
       * Duplicar projeto é o gesto de quem monta um "projeto modelo", que é
       * exatamente o que quem chega do Trello faz na primeira semana. Sem esta
       * cópia, o clone nascia com o quadro padrão de cinco colunas e todo
       * cartão empilhado na primeira, porque `status` apontava para colunas
       * que não existiam no projeto novo.
       *
       * Preservar o id textual é o que dispensa qualquer remapeamento: o
       * `status` copiado logo abaixo continua apontando para a coluna certa.
       * Com id UUID por projeto, cada duplicação precisaria de um mapa
       * idAntigo -> idNovo, e o dia em que alguém esquecesse de aplicá-lo todo
       * cartão cairia calado na primeira coluna.
       */
      try {
        await kanbanColumnService.clonarPara(projectId, newProject!.id);
      } catch (err) {
        console.warn('[ProjectContext.duplicateProject] Falha ao clonar o quadro:', err);
      }

      if (originalTasks && originalTasks.length > 0) {
        // Criar mapeamento de IDs antigos para novos
        const idMapping: Record<string, string> = {};

        // Ordenar tarefas para criar pais antes dos filhos
        const sortedTasks = [...originalTasks].sort((a, b) => {
          // Tarefas sem pai vêm primeiro
          if (!a.parent_id && b.parent_id) return -1;
          if (a.parent_id && !b.parent_id) return 1;
          return (a.position || 0) - (b.position || 0);
        });

        // Criar cada tarefa no novo projeto
        for (const task of sortedTasks) {
          const newParentId = task.parent_id ? idMapping[task.parent_id] : null;
          const newAssigned = task.assigned ? (teamIdMapping[task.assigned] || null) : null;

          const newTask = await taskService.create({
            project_id: newProject!.id,
            parent_id: newParentId,
            name: task.name,
            type: task.type || 'task',
            duration: task.duration || '1d',
            start_date: task.start_date,
            end_date: task.end_date,
            progress: task.progress || 0,
            color: task.color,
            assigned: newAssigned,
            estimated_hours: task.estimated_hours ?? null,
            priority: task.priority || 0,
            description: task.description || '',
            dependencies: [], // Não copiar dependências pois os IDs serão diferentes
            position: task.position || 0,
            expanded: task.expanded !== false,
            formatting: task.formatting,
            custom_fields: task.custom_fields,
            // `status`, `kanban_position` e `sprint_id` nunca foram copiados:
            // duplicar projeto jogava todo cartão para "Não iniciada" mantendo
            // o progresso, então o clone já nascia com Gantt e Kanban
            // contando histórias diferentes. Com o quadro clonado logo acima,
            // o id de coluna copiado cru continua válido.
            status: task.status,
            kanban_position: task.kanban_position ?? 0
          });

          // Mapear ID antigo para novo
          idMapping[task.id] = newTask!.id;
        }
      }

      // Copiar alocações (PLANO de capacidade) do projeto original para o novo
      if (Object.keys(teamIdMapping).length > 0) {
        try {
          const originalAllocations = await capacityService.getAllocationsByProject(projectId);
          const newAllocs = originalAllocations
            .filter(a => !a.is_simulation)
            .map(a => ({
              resource_id: a.resource_id,
              project_id: newProject!.id,
              role_in_project: a.role_in_project,
              hours_per_week: a.hours_per_week,
              start_date: a.start_date,
              end_date: a.end_date,
              is_simulation: false,
              notes: a.notes,
            }));
          if (newAllocs.length > 0) {
            await capacityService.bulkCreateAllocations(newAllocs);
          }
        } catch (allocErr) {
          console.warn('[ProjectContext.duplicateProject] Falha ao copiar alocações:', allocErr);
        }
      }

      // Adicionar novo projeto à lista
      setProjects(prev => [newProject as ProjectWithRole, ...prev]);

      // Trocar para o novo projeto
      setCurrentProjectId(newProject!.id);

      setSaving(false);
      analytics.trackProjectDuplicated();
      return newProject as ProjectWithRole;
    } catch (err: unknown) {
      // Ignorar AbortError - causado pelo React StrictMode
      const e = err as Error;
      if (e?.message?.includes('AbortError') || e?.name === 'AbortError') {
        console.log('[ProjectContext] duplicateProject AbortError ignorado');
        setSaving(false);
        return null;
      }
      console.error('Erro ao duplicar projeto:', err);
      setError(e.message);
      setSaving(false);
      throw err;
    }
  };

  const toggleProjectFavorite = async (projectId: string): Promise<void> => {
    // Optimistic update
    setProjects(prev => prev.map(p =>
      p.id === projectId ? { ...p, isFavorited: !p.isFavorited } : p
    ));
    try {
      await projectService.toggleFavorite(projectId);
    } catch (err) {
      // Reverter em caso de erro
      setProjects(prev => prev.map(p =>
        p.id === projectId ? { ...p, isFavorited: !p.isFavorited } : p
      ));
      console.error('Erro ao favoritar projeto:', err);
    }
  };

  /**
   * Guarda (ou tira) o projeto de uma pasta. `folderId` nulo devolve para "sem
   * pasta".
   *
   * Otimista como o favorito acima, porque a sidebar precisa reagir no mesmo
   * quadro do clique. O banco tem a ultima palavra: o trigger
   * `enforce_project_folder_scope` recusa pasta de outro escopo, e ai a lista
   * volta ao que era.
   */
  const moveProjectToFolder = async (projectId: string, folderId: string | null): Promise<void> => {
    const previous = projects.find(p => p.id === projectId)?.folder_id ?? null;
    if (previous === folderId) return;

    setProjects(prev => prev.map(p =>
      p.id === projectId ? { ...p, folder_id: folderId } : p
    ));
    try {
      await projectService.update(projectId, { folder_id: folderId });
    } catch (err) {
      setProjects(prev => prev.map(p =>
        p.id === projectId ? { ...p, folder_id: previous } : p
      ));
      console.error('Erro ao mover projeto para pasta:', err);
      throw err;
    }
  };

  const importProject = async (
    data: { project: Partial<Project>; tasks?: Array<Partial<Task>>; columns?: KanbanColumn[] },
    opts?: { onProgress?: (done: number, total: number) => void; onWarning?: (msg: string) => void }
  ): Promise<ProjectWithRole | null> => {
    // Verificar limite de projetos do plano
    if (!isSystemAdmin && !checkCanCreateProject()) {
      throw new Error('PLAN_LIMIT_REACHED');
    }

    // Para rollback: se a criação de tarefas falhar no meio, removemos o projeto
    // parcial (o cascade ON DELETE apaga as tarefas já inseridas).
    let createdProjectId: string | null = null;

    try {
      setSaving(true);

      const projectData = data.project;
      if (!projectData || !projectData.name) {
        throw new Error(getCurrentCopy().common.project.invalidData);
      }

      // Criar novo projeto com dados importados
      const newProject = await projectService.create({
        name: projectData.name,
        description: projectData.description || '',
        settings: projectData.settings || {}
      }, user!.id);
      createdProjectId = newProject!.id;

      /**
       * O QUADRO vem antes das tarefas.
       *
       * A ordem não é estética: as tarefas chegam com `status` apontando para
       * os ids destas colunas. Gravá-las depois deixaria, entre um passo e
       * outro, todo cartão apontando para coluna inexistente, e um erro no
       * meio da importação congelaria o projeto nesse estado.
       *
       * O trigger do banco já semeou as cinco canônicas quando o projeto
       * nasceu; `saveAll` sobrescreve com as colunas de origem e apaga as que
       * não vieram. Falhar aqui NÃO derruba a importação: o projeto fica com o
       * quadro padrão, os cartões caem na primeira coluna e o usuário refaz o
       * fluxo no editor, que é bem melhor do que perder a importação inteira.
       */
      if (data.columns && data.columns.length > 0) {
        try {
          await kanbanColumnService.saveAll(newProject!.id, data.columns);
        } catch (err) {
          console.error('[importProject] colunas do quadro falharam:', err);
          opts?.onWarning?.(getCurrentCopy().common.project.importColumnsFailed);
        }
      }

      // Importar tarefas se existirem
      const importedTasks = data.tasks || [];
      if (importedTasks.length > 0) {
        // Nó de importação: pode trazer children[] (árvore) e dependencyTempIds (transiente).
        type ImportTask = Partial<Task> & { children?: Array<Partial<Task>>; dependencyTempIds?: string[] };
        const idMapping: Record<string, string> = {};
        const depTempIdsByTempId: Record<string, string[]> = {};

        // Achatar (remove children[]); funciona tanto para árvore quanto flat.
        const flattenImportTasks = (taskList: Array<ImportTask>): Array<ImportTask> => {
          let result: Array<ImportTask> = [];
          for (const task of taskList) {
            const { children, ...taskData } = task;
            result.push(taskData);
            if (children && children.length > 0) {
              result = result.concat(flattenImportTasks(children as ImportTask[]));
            }
          }
          return result;
        };
        const flat = flattenImportTasks(importedTasks as ImportTask[]);

        // Agrupar por profundidade (cadeia de parent_id) — garante pai-antes-de-filho
        // mesmo quando 'position' não reflete a pré-ordem (ex.: backup JSON), e evita
        // o descarte silencioso de subárvores que o sort por posição podia causar.
        const byId = new Map<string, ImportTask>();
        for (const t of flat) if (t.id) byId.set(t.id, t);
        const depthOf = (t: ImportTask, guard = 0): number => {
          if (!t.parent_id || !byId.has(t.parent_id) || guard > 1000) return 0;
          return 1 + depthOf(byId.get(t.parent_id)!, guard + 1);
        };
        const byDepth: Array<Array<ImportTask>> = [];
        for (const t of flat) {
          const d = depthOf(t);
          (byDepth[d] ||= []).push(t);
        }

        const total = flat.length;
        let done = 0;
        let skipped = 0;

        // Inserir nível a nível, em LOTE (createMany): 1 round-trip por nível em vez
        // de 1 por tarefa, e o pai já está em idMapping quando o filho é criado.
        for (let d = 0; d < byDepth.length; d++) {
          const levelTasks = (byDepth[d] || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
          const toCreate: Parameters<typeof taskService.createMany>[0] = [];
          const tempIds: string[] = [];
          for (const t of levelTasks) {
            const newParentId = t.parent_id ? idMapping[t.parent_id] : null;
            // Safety net: pai inconsistente (não deve ocorrer com ordem topológica).
            if (t.parent_id && !newParentId) { skipped++; continue; }
            if (t.id && t.dependencyTempIds && t.dependencyTempIds.length > 0) {
              depTempIdsByTempId[t.id] = t.dependencyTempIds;
            }
            toCreate.push({
              project_id: newProject!.id,
              parent_id: newParentId,
              name: t.name || 'Nova Tarefa',
              type: t.type || 'task',
              duration: t.duration || '1d',
              start_date: t.start_date || t.startDate as string | undefined,
              end_date: t.end_date || t.endDate as string | undefined,
              progress: t.progress || 0,
              color: t.color,
              assigned: t.assigned,
              priority: t.priority || 0,
              description: t.description || '',
              dependencies: [],
              position: t.position || 0,
              expanded: t.expanded !== false,
              formatting: t.formatting,
              custom_fields: t.custom_fields || t.customFields,
              // Sem esta linha, toda tarefa importada nascia 'not_started'
              // (default de taskService.createMany) mesmo quando o parser tinha
              // deduzido progresso 100: o Gantt mostrava "Concluída" pelo
              // percentual e o Kanban mostrava "Não iniciada" pelo status, para
              // a mesma tarefa. Com quadro do Trello importado, é este campo
              // que põe cada cartão na coluna de onde ele veio.
              status: t.status
            });
            if (t.id) tempIds.push(t.id);
          }
          if (toCreate.length === 0) continue;
          const created = await taskService.createMany(toCreate);
          // createMany retorna as linhas na ordem de inserção.
          created.forEach((row, i) => { if (tempIds[i]) idMapping[tempIds[i]] = row.id; });
          done += created.length;
          opts?.onProgress?.(done, total);
        }

        // 2º passe: resolver dependências (precedências FS) para UUIDs reais agora
        // que idMapping está completo, e gravar em cada tarefa dependente.
        for (const [tempId, depTempIds] of Object.entries(depTempIdsByTempId)) {
          const realId = idMapping[tempId];
          if (!realId) continue;
          const realDeps = depTempIds.map(dt => idMapping[dt]).filter((x): x is string => !!x);
          if (realDeps.length > 0) {
            await taskService.update(realId, { dependencies: realDeps });
          }
        }

        if (skipped > 0) {
          console.warn(`[importProject] ${skipped} tarefa(s) ignoradas por pai inconsistente`);
          opts?.onWarning?.(`${skipped} tarefa(s) foram ignoradas por terem um pai inconsistente no arquivo.`);
        }
      }

      // Adicionar novo projeto à lista e selecionar
      setProjects(prev => [newProject as ProjectWithRole, ...prev]);
      setCurrentProjectId(newProject!.id);

      // Atualizar contagem de uso
      if (refreshUsage) refreshUsage();

      setSaving(false);
      analytics.trackProjectImported();
      return newProject as ProjectWithRole;
    } catch (err: unknown) {
      const e = err as Error;
      if (e?.message?.includes('AbortError') || e?.name === 'AbortError') {
        console.log('[ProjectContext] importProject AbortError ignorado');
        setSaving(false);
        return null;
      }
      // Rollback: não deixar projeto parcial órfão consumindo vaga do plano.
      if (createdProjectId) {
        try { await projectService.delete(createdProjectId); }
        catch (delErr) { console.error('Falha no rollback do import:', delErr); }
      }
      console.error('Erro ao importar projeto:', err);
      setError(e.message);
      setSaving(false);
      throw err;
    }
  };

  const updateProjectSettings = useCallback(async (settings: Partial<ProjectSettings>): Promise<void> => {
    if (!currentProjectId) return;

    try {
      setSaving(true);
      const newSettings = { ...project?.settings, ...settings };
      const updated = await projectService.update(currentProjectId, {
        settings: newSettings
      });
      setProject(updated);
      setProjects(prev => prev.map(p => p.id === currentProjectId ? { ...p, ...updated } : p));

      // Recalcular todas as tarefas com as novas configurações usando regras centralizadas
      const dateSettings = getProjectDateSettings({ settings: newSettings } as Project);

      setTasks(prev => {
        let newTasks = [...prev];
        const flatList = flattenTasks(newTasks);

        // Usar função centralizada para recalcular todas as tarefas
        const updates = recalculateAllTasks(flatList, dateSettings);

        // Aplicar updates e salvar no banco
        updates.forEach((update) => {
          newTasks = updateTaskInTree(newTasks, update.taskId, {
            startDate: update.startDate!,
            endDate: update.endDate!
          });

          // Salvar no banco
          taskService.update(update.taskId, {
            startDate: update.startDate!,
            endDate: update.endDate!
          }).catch((err: Error) => {
            console.error('Erro ao atualizar tarefa:', err);
          });
        });

        return newTasks;
      });

      setSaving(false);
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Erro ao atualizar configurações:', err);
      setError(e.message);
      setSaving(false);
    }
  }, [currentProjectId, project]);

  // Task operations
  const updateTask = useCallback(async (taskId: string, updates: TaskUpdateInput, options?: { undo?: boolean }): Promise<void> => {
    // Usar regras de negócio centralizadas
    const settings = getProjectDateSettings(project as Project);

    // Snapshot para undo (antes de qualquer mutação). Pulado durante batch (ex:
    // arraste no Gantt) ou quando o caller pede explicitamente { undo: false }.
    const undoBefore = options?.undo === false ? null : captureBefore();

    // Update local state immediately
    setTasks(prev => {
      const flatList = flattenTasks(prev);
      const task = flatList.find((t: Task) => t.id === taskId);

      if (!task) return prev;

      // Processar update com regras de negócio centralizadas
      const { taskUpdates, cascadeUpdates } = processTaskUpdate(task, updates as TaskDateUpdates, flatList, settings);

      // [CFBUG] log temporário — remover após validar bug de duas colunas customizadas
      if ((updates as Record<string, unknown>).customFields !== undefined) {
        console.log('[CFBUG] taskUpdates após processTaskUpdate', { taskId, customFields: (taskUpdates as Record<string, unknown>).customFields });
      }

      // Sync duration ↔ estimated_hours:
      //   Se a duração foi alterada e estimated_hours ainda está vazio/zero, derivar horas a partir
      //   do calendário do projeto. Se estimated_hours tem valor explícito, NÃO sobrescreve (o usuário
      //   definiu um esforço diferente da duração; respeitar a intenção).
      if ('duration' in taskUpdates || 'duration' in updates) {
        const newDuration = (taskUpdates.duration ?? (updates as { duration?: string }).duration) as string | null | undefined;
        const currentEstHours = typeof task.estimated_hours === 'number' ? task.estimated_hours : 0;
        const estHoursExplicit = ('estimated_hours' in updates) && Number((updates as { estimated_hours?: number }).estimated_hours) > 0;
        if (newDuration && !estHoursExplicit && currentEstHours === 0 && task.type !== 'milestone' && task.type !== 'group') {
          const projectSettings = (project as Project | null)?.settings ?? null;
          const workDaysPerWeek = projectSettings?.workDays?.length ?? 5;
          const hoursPerDay = projectSettings?.hoursPerDay ?? 8;
          // Inline parse — evita import cycle com utils/duration
          const match = String(newDuration).trim().match(/^([\d.]+)\s*([dwhm]?)$/i);
          if (match) {
            const value = parseFloat(match[1]);
            const unit = (match[2] || 'd').toLowerCase();
            let derived = 0;
            if (unit === 'h') derived = value;
            else if (unit === 'd') derived = value * hoursPerDay;
            else if (unit === 'w') derived = value * workDaysPerWeek * hoursPerDay;
            else if (unit === 'm') derived = value * 4 * workDaysPerWeek * hoursPerDay;
            else derived = value * hoursPerDay;
            if (derived > 0) {
              (taskUpdates as Record<string, unknown>).estimated_hours = Math.round(derived * 10) / 10;
            }
          }
        }
      }

      // Progresso mudou: a coluna acompanha, mas só quando ela é DERIVADA.
      //
      // Este bloco era o pior acoplamento do produto. Ele reescrevia
      // `tasks.status` com um de três ids literais a cada mudança de
      // progresso vinda de QUALQUER tela (barra do Gantt, célula da tabela,
      // slider do card, MCP, botão Concluir do Hoje). Com coluna livre isso
      // seria destrutivo: o cartão que a agência pôs em "Publicado" voltava
      // sozinho para "Em andamento" na primeira vez que alguém tocasse no
      // progresso, sem aviso e sem log.
      //
      // A regra agora olha a CATEGORIA da coluna atual:
      //  - categoria `on_hold` nunca é recalculada (era o antigo par
      //    pausado/bloqueado, e continua se comportando igual);
      //  - se a coluna atual JÁ é da categoria que o progresso implica, ela
      //    fica onde está. É o que mantém "Publicado" em Publicado aos 100%;
      //  - só quando a categoria muda de verdade o cartão anda, e anda para a
      //    primeira coluna daquela categoria NO QUADRO, nunca para um id fixo.
      if ('progress' in taskUpdates && !('status' in updates)) {
        const colunas = kanbanColumnsRef.current;
        const newProgress = Number(taskUpdates.progress) || 0;
        const currentStatus = (task as unknown as Record<string, unknown>).status as string | null;
        const categoriaAtual = categoryOf(currentStatus, colunas);

        if (categoriaAtual !== 'on_hold') {
          const categoriaAlvo: ColumnCategory =
            newProgress <= 0 ? 'not_started' : newProgress >= 100 ? 'completed' : 'in_progress';

          if (categoriaAtual !== categoriaAlvo) {
            const destino = firstColumnOfCategory(colunas, categoriaAlvo);
            if (destino) {
              (taskUpdates as Record<string, unknown>).status = destino.id;
            }
          }
        }
      }

      // Coluna escolhida à mão (arrastar o cartão, escolher no card, mover no
      // Hoje). O progresso acompanha a categoria da coluna de destino, e a
      // tabela de regras vive num lugar só: `progressForCategory`.
      if ('status' in updates) {
        const newStatus = updates.status as string;
        (taskUpdates as Record<string, unknown>).status = newStatus;
        if (!('progress' in updates)) {
          const categoria = categoryOf(newStatus, kanbanColumnsRef.current);
          if (categoria) {
            const alvo = progressForCategory(categoria, Number(task.progress) || 0);
            if (alvo !== null) {
              (taskUpdates as Record<string, unknown>).progress = alvo;
            }
          }
        }
      }

      // Aplicar update principal
      let newTasks = updateTaskInTree(prev, taskId, taskUpdates as Partial<Task>);

      // Salvar update principal no banco
      taskService.update(taskId, taskUpdates as TaskUpdateInput).catch((err: Error) => {
        console.error('Erro ao atualizar tarefa:', err);
      });

      // Sync assigned → RACI (Gantt → RACI) + Junction table + Notification
      if ('assigned' in updates && currentProjectId) {
        const newAssigned = updates.assigned;
        const oldAssigned = task.assigned;
        // Check if oldAssigned is a valid UUID (not a legacy name)
        const isUuid = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
        if (newAssigned !== oldAssigned) {
          // Remove old R (only if valid UUID)
          if (oldAssigned && isUuid(oldAssigned)) {
            raciService.removeAssignment(taskId, oldAssigned, 'R').catch(() => {});
            taskAssignmentService.removeAssignee(taskId, oldAssigned).catch(() => {});
          }
          // Set new R + junction primary + create notification
          if (newAssigned) {
            raciService.setAssignment({
              project_id: currentProjectId,
              task_id: taskId,
              team_member_id: newAssigned,
              raci_role: 'R'
            }).catch(() => {});

            // Sync junction table — add as primary
            taskAssignmentService.addAssignee(taskId, newAssigned, true).catch(() => {});

            // Update local map
            setTaskAssignmentsMap(prev => {
              const current = prev[taskId] || [];
              const withoutOld = oldAssigned ? current.filter(id => id !== oldAssigned) : current;
              const withoutNew = withoutOld.filter(id => id !== newAssigned);
              return { ...prev, [taskId]: [newAssigned, ...withoutNew] };
            });

            // Notification handled by DB trigger (trg_notify_task_assigned)
          } else {
            // Cleared assignment — update local map
            setTaskAssignmentsMap(prev => {
              const current = prev[taskId] || [];
              const withoutOld = oldAssigned ? current.filter(id => id !== oldAssigned) : current;
              if (withoutOld.length === 0) {
                const next = { ...prev };
                delete next[taskId];
                return next;
              }
              return { ...prev, [taskId]: withoutOld };
            });
          }
        }
      }

      // O bloco que sincronizava `kanban_status` saiu daqui em 24/08/2026.
      //
      // Ele gravava, a cada mudança de progresso, um vocabulário que nenhuma
      // parte do produto usava ('done', 'todo', 'review') num campo que nenhum
      // quadro lia. Era uma segunda fonte de verdade sobre a coluna da tarefa,
      // escrita em silêncio (o catch era vazio), e inflava de quebra a métrica
      // `tasks_with_kanban` do BI, que conta `kanban_status IS NOT NULL` como
      // sinal de que a pessoa usou o Kanban.
      //
      // Uma coluna só responde onde o cartão está: `tasks.status`, resolvida
      // contra `kanban_columns`.

      // Aplicar cascata para tarefas sucessoras
      cascadeUpdates.forEach((update) => {
        newTasks = updateTaskInTree(newTasks, update.taskId, {
          startDate: update.startDate!,
          endDate: update.endDate!
        });

        // Salvar alteração da tarefa sucessora no banco
        taskService.update(update.taskId, {
          startDate: update.startDate!,
          endDate: update.endDate!
        }).catch((err: Error) => console.error('Erro ao atualizar sucessora:', err));
      });

      // Propagar alterações para tarefas pai (lógica de Épico)
      // Recalcular datas e progresso dos ancestrais
      // Importante: propagar tanto para a tarefa original quanto para as cascatas
      const updatedFlatList = flattenTasks(newTasks);

      // Coletar todos os IDs que precisam propagar para seus pais
      const cascadeTaskIds = (cascadeUpdates || []).map((u: { taskId: string }) => u.taskId);
      const taskIdsToPropagate = [taskId, ...cascadeTaskIds];

      // Usar Set para evitar processar o mesmo pai múltiplas vezes
      const processedParentIds = new Set<string>();
      const allParentUpdates: CascadeUpdate[] = [];

      taskIdsToPropagate.forEach((tid: string) => {
        const parentPropagation = propagateToParents(updatedFlatList, tid, settings);
        parentPropagation.forEach((u) => {
          if (!processedParentIds.has(u.taskId)) {
            processedParentIds.add(u.taskId);
            allParentUpdates.push(u);
          }
        });
      });

      const parentUpdates = allParentUpdates;

      parentUpdates.forEach(update => {
        // Auto-calculate parent status from propagated progress
        const parentProgress = Number(update.progress) || 0;
        const parentStatus = parentProgress <= 0 ? 'not_started' : parentProgress >= 100 ? 'completed' : 'in_progress';

        newTasks = updateTaskInTree(newTasks, update.taskId, {
          startDate: update.startDate,
          endDate: update.endDate,
          duration: update.duration,
          progress: update.progress,
          status: parentStatus
        } as Partial<Task>);

        // Salvar alteração do pai no banco
        taskService.update(update.taskId, {
          start_date: update.startDate,
          end_date: update.endDate,
          duration: update.duration,
          progress: update.progress,
          status: parentStatus
        } as TaskUpdateInput).catch((err: Error) => console.error('Erro ao atualizar pai:', err));
      });

      return newTasks;
    });

    // Registrar no histórico de undo (ignora toggles de view-state como expanded).
    const keys = Object.keys(updates as Record<string, unknown>);
    const expandedOnly = keys.length > 0 && keys.every(k => k === 'expanded');
    if (!expandedOnly) maybePushUndo(describeUpdate(updates as Record<string, unknown>), undoBefore);
  }, [project, captureBefore, maybePushUndo]);

  const deleteTask = useCallback(async (taskId: string): Promise<void> => {
    // Encontrar a tarefa e seus filhos antes de deletar
    const flatList = flattenTasks(tasks);
    const taskToDelete = flatList.find((t: Task) => t.id === taskId);

    if (!taskToDelete) return;

    const undoBefore = captureBefore();

    // Se a tarefa tem filhos, mover para o avô (ou raiz)
    const childrenList = flatList.filter((t: Task) => t.parentId === taskId);
    const newParentId = taskToDelete.parentId || null;

    // Atualizar parent_id dos filhos no banco antes de deletar
    for (const child of childrenList) {
      try {
        await taskService.update(child.id, { parent_id: newParentId });
      } catch (err) {
        console.error('Erro ao mover filho:', err);
      }
    }

    // Atualizar a árvore local
    setTasks(prev => {
      let newTasks = [...prev];

      // Primeiro, mover os filhos para o novo pai
      if (childrenList.length > 0) {
        newTasks = moveChildrenToNewParent(newTasks, taskId, newParentId);
      }

      // Depois, deletar a tarefa
      return deleteTaskFromTree(newTasks, taskId);
    });

    if (selectedTaskId === taskId) {
      setSelectedTaskId(null);
    }

    try {
      await taskService.delete(taskId);
      analytics.trackTaskDeleted();
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Erro ao deletar tarefa:', err);
      setError(e.message);
    }

    maybePushUndo('Excluir tarefa', undoBefore);
  }, [selectedTaskId, tasks, captureBefore, maybePushUndo]);

  // Função auxiliar para mover filhos para novo pai
  const moveChildrenToNewParent = (tasksList: Task[], oldParentId: string, newParentId: string | null): Task[] => {
    // Encontrar os filhos do pai antigo
    const findAndRemoveChildren = (taskList: Task[]): { remaining: Task[]; children: Task[] } => {
      let removedChildren: Task[] = [];
      const remaining = taskList.map(task => {
        if (task.id === oldParentId && task.children) {
          removedChildren = [...task.children];
          return { ...task, children: [] as Task[] };
        }
        if (task.children) {
          const result = findAndRemoveChildren(task.children);
          removedChildren = [...removedChildren, ...result.children];
          return { ...task, children: result.remaining };
        }
        return task;
      });
      return { remaining, children: removedChildren };
    };

    const { remaining, children: removedChildren } = findAndRemoveChildren(tasksList);

    if (removedChildren.length === 0) return remaining;

    // Adicionar filhos ao novo pai
    if (newParentId === null) {
      // Adicionar à raiz
      return [...remaining, ...removedChildren];
    } else {
      // Adicionar como filhos do novo pai
      const addToParent = (taskList: Task[]): Task[] => {
        return taskList.map(task => {
          if (task.id === newParentId) {
            return {
              ...task,
              children: [...(task.children || []), ...removedChildren],
              expanded: true
            };
          }
          if (task.children) {
            return { ...task, children: addToParent(task.children) };
          }
          return task;
        });
      };
      return addToParent(remaining);
    }
  };

  const addTask = useCallback(async (parentId: string | null = null, taskData: Partial<Task> = {}): Promise<string | null> => {
    if (!currentProjectId) return null;

    const undoBefore = captureBefore();

    // Usar regras de negócio para garantir que a data inicial seja um dia útil
    const settings = getProjectDateSettings(project as Project);
    const startDate = ensureWorkDay(taskData.startDate || new Date(), settings.workDays, settings.holidays);

    // Calcular próxima posição entre irmãos para manter ordem
    let nextPosition = taskData.position;
    if (nextPosition === undefined || nextPosition === null) {
      if (parentId) {
        const flatList = flattenTasks(tasks);
        const parent = flatList.find((t: Task) => t.id === parentId);
        const siblings = parent?.children || [];
        nextPosition = siblings.length > 0
          ? Math.max(...siblings.map((s: Task) => s.position || 0)) + 1
          : 0;
      } else {
        nextPosition = tasks.length > 0
          ? Math.max(...tasks.map(t => t.position || 0)) + 1
          : 0;
      }
    }

    const newTaskData = {
      project_id: currentProjectId,
      parent_id: parentId,
      name: taskData.name || 'Nova Tarefa',
      duration: taskData.duration || '1d',
      start_date: startDate,
      end_date: taskData.endDate || startDate,
      progress: taskData.progress || 0,
      color: taskData.color || null,
      type: taskData.type || 'task',
      assigned: taskData.assigned || null,
      priority: taskData.priority || 0,
      description: taskData.description || '',
      position: nextPosition,
      status: taskData.status ?? 'not_started',
      kanban_status: taskData.kanban_status ?? null,
      kanban_position: taskData.kanban_position ?? 0,
      sprint_id: taskData.sprint_id ?? null,
      // Necessários para criar cartão a partir de template (checklist/labels
      // vivem em custom_fields; estimativa é coluna canônica de horas).
      custom_fields: taskData.custom_fields ?? {},
      estimated_hours: taskData.estimated_hours ?? null
    };

    try {
      const created = await taskService.create(newTaskData);

      // Convert to app format
      const newTask: Task = {
        ...created!,
        startDate: parseDateFromDB(created!.start_date) || new Date(),
        endDate: parseDateFromDB(created!.end_date) || new Date(),
        customFields: created!.custom_fields || {},
        dependencyLags: created!.dependency_lags || {},
        children: []
      } as Task;

      setTasks(prev => addTaskToTree(prev, parentId, newTask));
      maybePushUndo('Adicionar tarefa', undoBefore);
      analytics.trackTaskCreated('manual');
      return created!.id;
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Erro ao criar tarefa:', err);
      setError(e.message);
      return null;
    }
  }, [currentProjectId, tasks, project, captureBefore, maybePushUndo]);

  const toggleExpand = useCallback((taskId: string): void => {
    setTasks(prev => toggleTaskExpansion(prev, taskId));

    // Update in database
    const task = findTask(tasks, taskId);
    if (task) {
      taskService.update(taskId, { expanded: !task.expanded }).catch(console.error);
    }
  }, [tasks]);

  const reorderTasks = useCallback(async (fromIndex: number, toIndex: number, flatTasksList: Task[] | null = null, position: string = 'before'): Promise<void> => {
    if (fromIndex === toIndex) return;
    if (!flatTasksList || flatTasksList.length === 0) return;

    const movedTask = flatTasksList[fromIndex];
    const targetTask = flatTasksList[toIndex];

    if (!movedTask || !targetTask || movedTask.id === targetTask.id) return;

    // Bloquear soltar uma tarefa dentro da própria subárvore (geraria ciclo).
    if (isAncestorOf(flatTasksList, movedTask.id, targetTask.id)) {
      console.warn('Movimento inválido: destino é descendente da tarefa movida');
      return;
    }

    const undoBefore = captureBefore();
    const oldParentId = movedTask.parentId ?? null;
    const newParentId = targetTask.parentId ?? null;
    const parentChanged = oldParentId !== newParentId;
    const dropPos = (position === 'after' ? 'after' : 'before') as 'before' | 'after';

    setTasks(prev => {
      // Mover adotando o pai do destino (padrão MS Project / Jira: a posição de
      // soltura define a participação na hierarquia). Cobre tanto reordenar entre
      // irmãs quanto tirar/mover a subtarefa entre épicos.
      const moved = moveTaskToTarget(prev, movedTask.id, targetTask.id, dropPos);
      if (!moved) return prev;
      let newTasks: Task[] = moved;

      // Renumerar position dentro de cada grupo de irmãos e persistir mudanças.
      const renumber = (list: Task[]): Task[] => list.map((task, index) => {
        if (task.position !== index) {
          taskService.update(task.id, { position: index }).catch(console.error);
        }
        const children = task.children && task.children.length > 0 ? renumber(task.children) : task.children;
        return { ...task, position: index, children } as Task;
      });
      newTasks = renumber(newTasks);

      if (parentChanged) {
        // Persistir o novo pai
        taskService.update(movedTask.id, { parent_id: newParentId }).catch(console.error);

        const flatList = flattenTasks(newTasks);

        // Limpar predecessoras que ficaram inválidas após a mudança de hierarquia
        const cleanResult = cleanInvalidPredecessors(movedTask.id, flatList);
        if (cleanResult) {
          newTasks = updateTaskInTree(newTasks, movedTask.id, { dependencies: cleanResult.cleanedDependencies });
          taskService.update(movedTask.id, { dependencies: cleanResult.cleanedDependencies }).catch(console.error);
        }

        // Se o novo pai virou épico (ganhou uma filha), ele perde predecessoras
        // próprias (épicos têm datas calculadas pelas filhas).
        if (newParentId) {
          const newParent = flatList.find((t: Task) => t.id === newParentId);
          if (newParent && newParent.dependencies && newParent.dependencies.length > 0) {
            newTasks = updateTaskInTree(newTasks, newParentId, { dependencies: [] });
            taskService.update(newParentId, { dependencies: [] }).catch(console.error);
          }
        }

        // Propagar datas/progresso para os pais afetados (novo e antigo).
        const settings = getProjectDateSettings(project as Project);
        const applyPropagation = (childId: string): void => {
          const flat = flattenTasks(newTasks);
          const updates = propagateToParents(flat, childId, settings);
          updates.forEach((update) => {
            newTasks = updateTaskInTree(newTasks, update.taskId, {
              startDate: update.startDate!,
              endDate: update.endDate!,
              duration: update.duration,
              progress: update.progress
            });
            taskService.update(update.taskId, {
              startDate: update.startDate!,
              endDate: update.endDate!,
              duration: update.duration,
              progress: update.progress
            }).catch(console.error);
          });
        };

        if (newParentId) applyPropagation(movedTask.id);
        if (oldParentId) {
          const remaining = flattenTasks(newTasks).filter((t: Task) => t.parentId === oldParentId);
          if (remaining.length > 0) applyPropagation(remaining[0].id);
        }
      }

      return newTasks;
    });

    maybePushUndo(parentChanged ? 'Mover tarefa' : 'Reordenar tarefa', undoBefore);
  }, [project, captureBefore, maybePushUndo]);

  const duplicateTaskItem = useCallback(async (taskId: string): Promise<void> => {
    const taskToDuplicate = findTask(tasks, taskId);
    if (!taskToDuplicate || !currentProjectId) return;

    const undoBefore = captureBefore();

    // Obter parentId da lista flat (a árvore não tem parentId)
    const flatList = flattenTasks(tasks);
    const taskInFlatList = flatList.find((t: Task) => t.id === taskId);
    const parentId = taskInFlatList?.parentId || null;

    // Duplicar localmente (com novos IDs temporários)
    const duplicatedTree = duplicateTask(taskToDuplicate);

    try {
      // Função recursiva para criar tarefas no banco
      const createTaskWithChildren = async (task: Task, taskParentId: string | null = null): Promise<Task> => {
        const created = await taskService.create({
          project_id: currentProjectId,
          parent_id: taskParentId,
          name: task.name,
          type: task.type,
          duration: task.duration,
          start_date: task.startDate,
          end_date: task.endDate,
          progress: task.progress || 0,
          color: task.color,
          assigned: task.assigned,
          priority: task.priority || 0,
          description: task.description || '',
          dependencies: [], // Não copiar dependências para evitar referências inválidas
          position: task.position || 0,
          expanded: task.expanded !== false
        });

        const taskWithDates: Task = {
          ...created!,
          startDate: parseDateFromDB(created!.start_date) || new Date(),
          endDate: parseDateFromDB(created!.end_date) || new Date(),
          customFields: created!.custom_fields || {},
          dependencyLags: created!.dependency_lags || {},
          children: []
        } as Task;

        // Criar filhos recursivamente
        if (task.children && task.children.length > 0) {
          for (const child of task.children) {
            const createdChild = await createTaskWithChildren(child, created!.id);
            taskWithDates.children.push(createdChild);
          }
        }

        return taskWithDates;
      };

      // Criar a tarefa raiz duplicada e seus filhos
      const createdTask = await createTaskWithChildren(duplicatedTree, parentId);

      // Adicionar ao state logo após a tarefa original (como irmã)
      setTasks(prev => {
        return addTaskAfterTask(prev, taskId, parentId, createdTask);
      });

      maybePushUndo('Duplicar tarefa', undoBefore);
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Erro ao duplicar tarefa:', err);
      setError(e.message);
    }
  }, [tasks, currentProjectId, captureBefore, maybePushUndo]);

  // Mover tarefa para cima
  const moveTaskUpHandler = useCallback(async (taskId: string): Promise<void> => {
    const undoBefore = captureBefore();
    // Pré-checar no-op fora de batch para não empilhar undo vazio (já no topo).
    const willChange = undoBatchRef.current ? true : moveTaskUp(tasksRef.current, taskId) !== tasksRef.current;

    setTasks(prev => {
      const newTasks = moveTaskUp(prev, taskId);
      if (newTasks === prev) return prev; // Não mudou

      // Persistir posições no banco
      const flatList = flattenTasks(newTasks);
      flatList.forEach((task: Task, index: number) => {
        taskService.update(task.id, { position: index }).catch(console.error);
      });

      return newTasks;
    });

    if (willChange) maybePushUndo('Mover tarefa', undoBefore);
  }, [captureBefore, maybePushUndo]);

  // Mover tarefa para baixo
  const moveTaskDownHandler = useCallback(async (taskId: string): Promise<void> => {
    const undoBefore = captureBefore();
    const willChange = undoBatchRef.current ? true : moveTaskDown(tasksRef.current, taskId) !== tasksRef.current;

    setTasks(prev => {
      const newTasks = moveTaskDown(prev, taskId);
      if (newTasks === prev) return prev; // Não mudou

      // Persistir posições no banco
      const flatList = flattenTasks(newTasks);
      flatList.forEach((task: Task, index: number) => {
        taskService.update(task.id, { position: index }).catch(console.error);
      });

      return newTasks;
    });

    if (willChange) maybePushUndo('Mover tarefa', undoBefore);
  }, [captureBefore, maybePushUndo]);

  // Aumentar recuo (indent)
  const indentTaskHandler = useCallback(async (taskId: string): Promise<void> => {
    const undoBefore = captureBefore();
    const willChange = undoBatchRef.current ? true : indentTask(tasksRef.current, taskId) !== null;

    setTasks(prev => {
      // Guardar pai antigo antes de mover
      const oldFlatList = flattenTasks(prev);
      const taskBefore = oldFlatList.find((t: Task) => t.id === taskId);
      const oldParentId = taskBefore?.parentId;

      let newTasks = indentTask(prev, taskId);
      if (!newTasks) return prev; // Não foi possível indentar

      // Encontrar o novo pai e atualizar parent_id no banco
      const flatList = flattenTasks(newTasks);
      const task = flatList.find((t: Task) => t.id === taskId);
      if (task) {
        taskService.update(taskId, { parent_id: task.parentId || null }).catch(console.error);

        // Validar e limpar predecessoras que se tornaram inválidas após a mudança de hierarquia
        const cleanResult = cleanInvalidPredecessors(taskId, flatList);
        if (cleanResult) {
          // Atualizar a tarefa com as dependências limpas
          taskService.update(taskId, { dependencies: cleanResult.cleanedDependencies }).catch(console.error);
        }

        // Se a tarefa tem um novo pai, verificar se o pai virou épico e limpar suas predecessoras
        // Épicos não podem ter predecessoras - suas datas são calculadas pelas filhas
        if (task.parentId) {
          const newParent = flatList.find((t: Task) => t.id === task.parentId);
          if (newParent && newParent.dependencies && newParent.dependencies.length > 0) {
            // O pai agora é um épico (tem filhas), deve perder suas predecessoras
            newTasks = updateTaskInTree(newTasks, task.parentId, { dependencies: [] });
            taskService.update(task.parentId, { dependencies: [] }).catch(console.error);
          }
        }
      }

      // Propagar progresso e datas para o novo pai (e ancestrais)
      const settings = getProjectDateSettings(project as Project);
      let finalTasks = newTasks;
      const updatedFlatList = flattenTasks(finalTasks);

      // Propagar para o novo pai
      const parentUpdates = propagateToParents(updatedFlatList, taskId, settings);
      parentUpdates.forEach((update) => {
        finalTasks = updateTaskInTree(finalTasks, update.taskId, {
          startDate: update.startDate!,
          endDate: update.endDate!,
          duration: update.duration,
          progress: update.progress
        });
        taskService.update(update.taskId, {
          startDate: update.startDate!,
          endDate: update.endDate!,
          duration: update.duration,
          progress: update.progress
        }).catch(console.error);
      });

      // Se tinha pai antigo, recalcular progresso dele também (perdeu uma filha)
      if (oldParentId) {
        const recalcFlatList = flattenTasks(finalTasks);
        const oldParent = recalcFlatList.find((t: Task) => t.id === oldParentId);
        if (oldParent) {
          const oldParentChildren = recalcFlatList.filter((t: Task) => t.parentId === oldParentId);
          if (oldParentChildren.length > 0) {
            const oldParentUpdates = propagateToParents(recalcFlatList, oldParentChildren[0].id, settings);
            oldParentUpdates.forEach((update) => {
              finalTasks = updateTaskInTree(finalTasks, update.taskId, {
                startDate: update.startDate!,
                endDate: update.endDate!,
                duration: update.duration,
                progress: update.progress
              });
              taskService.update(update.taskId, {
                startDate: update.startDate!,
                endDate: update.endDate!,
                duration: update.duration,
                progress: update.progress
              }).catch(console.error);
            });
          }
        }
      }

      return finalTasks;
    });

    if (willChange) maybePushUndo('Indentar tarefa', undoBefore);
  }, [project, captureBefore, maybePushUndo]);

  // Reduzir recuo (outdent)
  const outdentTaskHandler = useCallback(async (taskId: string): Promise<void> => {
    const undoBefore = captureBefore();
    const willChange = undoBatchRef.current ? true : outdentTask(tasksRef.current, taskId) !== null;

    setTasks(prev => {
      // Guardar pai antigo antes de mover
      const oldFlatList = flattenTasks(prev);
      const taskBefore = oldFlatList.find((t: Task) => t.id === taskId);
      const oldParentId = taskBefore?.parentId;

      const newTasks = outdentTask(prev, taskId);
      if (!newTasks) return prev; // Não foi possível fazer outdent

      // Encontrar o novo pai e atualizar parent_id no banco
      const flatList = flattenTasks(newTasks);
      const task = flatList.find((t: Task) => t.id === taskId);
      if (task) {
        taskService.update(taskId, { parent_id: task.parentId || null }).catch(console.error);

        // Validar e limpar predecessoras que se tornaram inválidas após a mudança de hierarquia
        const cleanResult = cleanInvalidPredecessors(taskId, flatList);
        if (cleanResult) {
          // Atualizar a tarefa com as dependências limpas
          taskService.update(taskId, { dependencies: cleanResult.cleanedDependencies }).catch(console.error);
        }
      }

      // Propagar progresso e datas para os pais afetados
      const settings = getProjectDateSettings(project as Project);
      let finalTasks = newTasks;

      // Recalcular pai antigo (perdeu uma filha)
      if (oldParentId) {
        const recalcFlatList = flattenTasks(finalTasks);
        const oldParent = recalcFlatList.find((t: Task) => t.id === oldParentId);
        if (oldParent) {
          const oldParentChildren = recalcFlatList.filter((t: Task) => t.parentId === oldParentId);
          if (oldParentChildren.length > 0) {
            // Recalcular usando uma das filhas restantes
            const oldParentUpdates = propagateToParents(recalcFlatList, oldParentChildren[0].id, settings);
            oldParentUpdates.forEach((update) => {
              finalTasks = updateTaskInTree(finalTasks, update.taskId, {
                startDate: update.startDate!,
                endDate: update.endDate!,
                duration: update.duration,
                progress: update.progress
              });
              taskService.update(update.taskId, {
                startDate: update.startDate!,
                endDate: update.endDate!,
                duration: update.duration,
                progress: update.progress
              }).catch(console.error);
            });
          } else {
            // Pai antigo não tem mais filhos - zerar progresso ou deixar como estava
            // Neste caso, o pai deixa de ser épico, então seu progresso fica como o usuário definir
          }
        }
      }

      // Propagar para o novo pai (se houver)
      if (task?.parentId) {
        const updatedFlatList = flattenTasks(finalTasks);
        const parentUpdates = propagateToParents(updatedFlatList, taskId, settings);
        parentUpdates.forEach((update) => {
          finalTasks = updateTaskInTree(finalTasks, update.taskId, {
            startDate: update.startDate!,
            endDate: update.endDate!,
            duration: update.duration,
            progress: update.progress
          });
          taskService.update(update.taskId, {
            startDate: update.startDate!,
            endDate: update.endDate!,
            duration: update.duration,
            progress: update.progress
          }).catch(console.error);
        });
      }

      return finalTasks;
    });

    if (willChange) maybePushUndo('Reduzir recuo', undoBefore);
  }, [project, captureBefore, maybePushUndo]);

  // Adicionar tarefa abaixo (como irmã)
  const addTaskBelow = useCallback(async (taskId: string): Promise<string | null> => {
    if (!currentProjectId) return null;

    const undoBefore = captureBefore();

    const flatList = flattenTasks(tasks);
    const taskIndex = flatList.findIndex((t: Task) => t.id === taskId);
    const referenceTask = flatList[taskIndex];

    if (!referenceTask) return null;

    // Usar regras de negócio para garantir que a data inicial seja um dia útil
    const settings = getProjectDateSettings(project as Project);
    const startDate = ensureWorkDay(new Date(), settings.workDays, settings.holidays);

    const newTaskData = {
      project_id: currentProjectId,
      parent_id: referenceTask.parentId || null,
      name: 'Nova Tarefa',
      duration: '1d',
      start_date: startDate,
      end_date: startDate,
      progress: 0,
      color: null,
      type: 'task' as const,
      assigned: null,
      priority: 0,
      description: '',
      position: taskIndex + 1
    };

    try {
      const created = await taskService.create(newTaskData);

      const newTask: Task = {
        ...created!,
        startDate: parseDateFromDB(created!.start_date) || new Date(),
        endDate: parseDateFromDB(created!.end_date) || new Date(),
        customFields: created!.custom_fields || {},
        dependencyLags: created!.dependency_lags || {},
        children: []
      } as Task;

      setTasks(prev => {
        // Inserir após a tarefa de referência no mesmo nível
        if (referenceTask.parentId) {
          // Inserir como filha do mesmo pai
          return addTaskAfterSiblingInTree(prev, referenceTask.parentId, taskId, newTask);
        } else {
          // Inserir na raiz após a tarefa de referência
          const rootIndex = prev.findIndex(t => t.id === taskId);
          const newTasks = [...prev];
          newTasks.splice(rootIndex + 1, 0, newTask);
          return newTasks;
        }
      });

      maybePushUndo('Adicionar tarefa', undoBefore);
      return created!.id;
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Erro ao criar tarefa:', err);
      setError(e.message);
      return null;
    }
  }, [currentProjectId, tasks, project, captureBefore, maybePushUndo]);

  // Função auxiliar para adicionar tarefa após irmão na árvore
  const addTaskAfterSiblingInTree = (tasksList: Task[], parentId: string, afterId: string, newTask: Task): Task[] => {
    return tasksList.map(task => {
      if (task.id === parentId) {
        const taskChildren = task.children || [];
        const afterIndex = taskChildren.findIndex((c: Task) => c.id === afterId);
        const newChildren = [...taskChildren];
        newChildren.splice(afterIndex + 1, 0, newTask);
        return { ...task, children: newChildren };
      }
      if (task.children) {
        return { ...task, children: addTaskAfterSiblingInTree(task.children, parentId, afterId, newTask) };
      }
      return task;
    });
  };

  // Adicionar múltiplas tarefas de uma vez (ex: paste do clipboard)
  // Usa taskService.createMany() para batch INSERT + um único setTasks()
  const addMultipleTasks = useCallback(async (
    parsedTasks: { name: string; startDate?: Date; endDate?: Date; duration?: string; progress?: number }[],
    afterTaskId?: string | null
  ): Promise<number> => {
    if (!currentProjectId || parsedTasks.length === 0) return 0;

    const undoBefore = captureBefore();
    const settings = getProjectDateSettings(project as Project);
    const flatList = flattenTasks(tasks);

    // Determinar parentId e posição inicial
    let parentId: string | null = null;
    let basePosition = tasks.length;

    if (afterTaskId) {
      const refTask = flatList.find((t: Task) => t.id === afterTaskId);
      if (refTask) {
        parentId = refTask.parentId || null;
        basePosition = (refTask.position || 0) + 1;
      }
    }

    // Montar dados para criar todas de uma vez
    const tasksToCreate = parsedTasks.map((parsed, i) => {
      const startDate = ensureWorkDay(parsed.startDate || new Date(), settings.workDays, settings.holidays);
      return {
        project_id: currentProjectId,
        parent_id: parentId,
        name: parsed.name,
        duration: parsed.duration || '1d',
        start_date: startDate,
        end_date: parsed.endDate || startDate,
        progress: parsed.progress || 0,
        position: basePosition + i,
        type: 'task' as const,
      };
    });

    try {
      const created = await taskService.createMany(tasksToCreate);

      // Converter para formato app
      const newTasks: Task[] = created.map(row => ({
        ...row,
        startDate: parseDateFromDB(row.start_date) || new Date(),
        endDate: parseDateFromDB(row.end_date) || new Date(),
        customFields: row.custom_fields || {},
        dependencyLags: row.dependency_lags || {},
        children: []
      } as Task));

      // Inserir todas no state de uma vez
      setTasks(prev => {
        if (parentId && afterTaskId) {
          // Inserir como filhas do mesmo pai, após afterTaskId
          let result = prev;
          let insertAfterId = afterTaskId;
          for (const newTask of newTasks) {
            result = addTaskAfterSiblingInTree(result, parentId!, insertAfterId, newTask);
            insertAfterId = newTask.id;
          }
          return result;
        } else if (afterTaskId) {
          // Inserir na raiz após a tarefa selecionada
          const idx = prev.findIndex(t => t.id === afterTaskId);
          const copy = [...prev];
          copy.splice(idx + 1, 0, ...newTasks);
          return copy;
        } else {
          // Inserir no final
          return [...prev, ...newTasks];
        }
      });

      maybePushUndo(created.length > 1 ? 'Colar tarefas' : 'Adicionar tarefa', undoBefore);
      return created.length;
    } catch (err) {
      console.error('Erro ao criar tarefas do clipboard:', err);
      return 0;
    }
  }, [currentProjectId, tasks, project, captureBefore, maybePushUndo]);

  // Column operations
  const addColumn = useCallback(async (type: string, name: string, options: Record<string, unknown> = {}): Promise<ColumnConfig> => {
    const newColumn = createColumn(type, name, {
      ...options,
      position: columns.length
    });

    const updatedColumns = [...columns, newColumn];
    setColumns(updatedColumns);

    // Persist to project settings
    try {
      await updateProjectSettings({ columns: updatedColumns });
    } catch (err) {
      console.error('Erro ao salvar coluna:', err);
    }

    return newColumn;
  }, [columns, updateProjectSettings]);

  const updateColumn = useCallback(async (columnId: string, updates: Partial<ColumnConfig>): Promise<void> => {
    const updatedColumns = columns.map(col =>
      col.id === columnId ? { ...col, ...updates } : col
    );
    setColumns(updatedColumns);

    // Persist to project settings
    try {
      await updateProjectSettings({ columns: updatedColumns });
    } catch (err) {
      console.error('Erro ao atualizar coluna:', err);
    }
  }, [columns, updateProjectSettings]);

  const deleteColumn = useCallback(async (columnId: string): Promise<boolean> => {
    const column = columns.find(c => c.id === columnId);
    if (!column || !column.deletable) {
      console.warn('Coluna não pode ser excluída:', columnId);
      return false;
    }

    const updatedColumns = columns.filter(c => c.id !== columnId);
    // Recalculate positions
    const repositionedColumns = updatedColumns.map((col, index) => ({
      ...col,
      position: index
    }));

    setColumns(repositionedColumns);

    // Persist to project settings
    try {
      await updateProjectSettings({ columns: repositionedColumns });
    } catch (err) {
      console.error('Erro ao excluir coluna:', err);
    }

    return true;
  }, [columns, updateProjectSettings]);

  const reorderColumns = useCallback(async (fromId: string, toId: string): Promise<void> => {
    if (fromId === toId) return;

    // Mover por ID dentro da ordem ordenada por position. O array `columns` não é
    // necessariamente ordenado nem contém só colunas visíveis; mover por índice cru
    // (como antes) desalinhava com a ordem renderizada e movia a coluna errada.
    const ordered = [...columns].sort((a, b) => a.position - b.position);
    const fromIdx = ordered.findIndex(c => c.id === fromId);
    const toIdx = ordered.findIndex(c => c.id === toId);
    if (fromIdx === -1 || toIdx === -1) return;

    const [removed] = ordered.splice(fromIdx, 1);
    ordered.splice(toIdx, 0, removed);

    // Reatribuir position sequencial a todas as colunas (preserva a posição
    // relativa das invisíveis).
    const repositionedColumns = ordered.map((col, index) => ({
      ...col,
      position: index
    }));

    setColumns(repositionedColumns);

    // Persist to project settings
    try {
      await updateProjectSettings({ columns: repositionedColumns });
    } catch (err) {
      console.error('Erro ao reordenar colunas:', err);
    }
  }, [columns, updateProjectSettings]);

  const toggleColumnVisibility = useCallback(async (columnId: string): Promise<void> => {
    const updatedColumns = columns.map(col =>
      col.id === columnId ? { ...col, visible: !col.visible } : col
    );
    setColumns(updatedColumns);

    // Persist to project settings
    try {
      await updateProjectSettings({ columns: updatedColumns });
    } catch (err) {
      console.error('Erro ao alternar visibilidade da coluna:', err);
    }
  }, [columns, updateProjectSettings]);

  const resizeColumn = useCallback(async (columnId: string, newWidth: number): Promise<void> => {
    const column = columns.find(c => c.id === columnId);
    if (!column) return;

    // Respect min/max width constraints
    const width = Math.max(column.minWidth || 50, Math.min(column.maxWidth || 500, newWidth));

    const updatedColumns = columns.map(col =>
      col.id === columnId ? { ...col, width } : col
    );
    setColumns(updatedColumns);

    // Persist to project settings (debounced in the component)
    try {
      await updateProjectSettings({ columns: updatedColumns });
    } catch (err) {
      console.error('Erro ao redimensionar coluna:', err);
    }
  }, [columns, updateProjectSettings]);

  const resetColumnsToDefault = useCallback(async (): Promise<void> => {
    // Use deep copy to ensure React detects the state change properly
    const defaultCols = DEFAULT_COLUMNS.map((col: ColumnConfig) => ({ ...col }));
    setColumns(defaultCols);

    try {
      await updateProjectSettings({ columns: defaultCols });
    } catch (err) {
      console.error('Erro ao resetar colunas:', err);
    }
  }, [updateProjectSettings]);

  // Get visible columns sorted by position
  const getVisibleColumns = useCallback((): ColumnConfig[] => {
    return columns
      .filter(col => col.visible)
      .sort((a, b) => a.position - b.position);
  }, [columns]);

  // Team operations
  const refreshTeam = async (): Promise<void> => {
    if (!currentProjectId) return;
    try {
      const teamData = await teamService.getByProject(currentProjectId);
      setTeam(teamData);
    } catch (err: unknown) {
      const e = err as Error;
      if (!e?.message?.includes('AbortError')) {
        console.error('[ProjectContext] Erro ao recarregar equipe:', err);
      }
    }
  };

  const addTeamMember = async (memberData: Partial<TeamMember>): Promise<TeamMember | null> => {
    if (!currentProjectId) return null;

    try {
      const created = await teamService.create({
        ...memberData,
        project_id: currentProjectId
      } as TeamMember);
      setTeam(prev => [...prev, created]);
      analytics.trackMemberAdded();
      return created;
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Erro ao adicionar membro:', err);
      setError(e.message);
      return null;
    }
  };

  const updateTeamMember = async (id: string, updates: Partial<TeamMember>): Promise<TeamMember | null> => {
    try {
      const updated = await teamService.update(id, updates);
      setTeam(prev => prev.map(m => m.id === id ? updated : m));
      return updated;
    } catch (err: unknown) {
      console.error('[ProjectContext] Erro ao atualizar membro:', err);
      return null;
    }
  };

  const deleteTeamMember = async (id: string, reassignTo?: string | null): Promise<boolean> => {
    try {
      // Reatribuir tarefas via junction table
      if (reassignTo) {
        await taskAssignmentService.reassignAll(id, reassignTo);
      } else {
        await taskAssignmentService.removeAllByMember(id);
      }

      // Also update legacy assigned field for tasks where this member was primary
      const flatTaskList = flattenTasks(tasks);
      const assignedTasks = flatTaskList.filter(t => t.assigned === id);
      if (assignedTasks.length > 0) {
        for (const task of assignedTasks) {
          await updateTask(task.id, { assigned: reassignTo ?? null } as Record<string, unknown>);
        }
      }

      // Update local assignments map
      setTaskAssignmentsMap(prev => {
        const next = { ...prev };
        for (const [taskId, members] of Object.entries(next)) {
          if (members.includes(id)) {
            const filtered = members.filter(m => m !== id);
            if (reassignTo && !filtered.includes(reassignTo)) {
              // Put reassignTo in the position where old member was
              const idx = members.indexOf(id);
              filtered.splice(idx, 0, reassignTo);
            }
            if (filtered.length === 0) {
              delete next[taskId];
            } else {
              next[taskId] = filtered;
            }
          }
        }
        return next;
      });

      await teamService.delete(id);
      setTeam(prev => prev.filter(m => m.id !== id));
      return true;
    } catch (err: unknown) {
      console.error('[ProjectContext] Erro ao remover membro:', err);
      return false;
    }
  };

  // ========= Multi-assignee operations =========

  const getTaskAssignees = useCallback((taskId: string): string[] => {
    return taskAssignmentsMap[taskId] || (tasks ? (() => {
      // Fallback: use legacy assigned field
      const flatList = flattenTasks(tasks);
      const task = flatList.find(t => t.id === taskId);
      return task?.assigned ? [task.assigned] : [];
    })() : []);
  }, [taskAssignmentsMap, tasks]);

  const addAssignee = async (taskId: string, memberId: string): Promise<void> => {
    if (!currentProjectId) return;
    try {
      const currentAssignees = taskAssignmentsMap[taskId] || [];
      const isPrimary = currentAssignees.length === 0;

      await taskAssignmentService.addAssignee(taskId, memberId, isPrimary);

      // If first assignee, also set legacy assigned field
      if (isPrimary) {
        await updateTask(taskId, { assigned: memberId } as Record<string, unknown>);
      }

      // Update local state
      setTaskAssignmentsMap(prev => {
        const current = prev[taskId] || [];
        if (current.includes(memberId)) return prev;
        return { ...prev, [taskId]: isPrimary ? [memberId, ...current] : [...current, memberId] };
      });

      // Notification handled by DB trigger (trg_notify_task_assigned)
    } catch (err) {
      console.error('[ProjectContext] Erro ao adicionar assignee:', err);
    }
  };

  const removeAssignee = async (taskId: string, memberId: string): Promise<void> => {
    if (!currentProjectId) return;
    try {
      await taskAssignmentService.removeAssignee(taskId, memberId);

      const currentAssignees = taskAssignmentsMap[taskId] || [];
      const wasPrimary = currentAssignees[0] === memberId;
      const remaining = currentAssignees.filter(id => id !== memberId);

      // If removed the primary, promote next or clear
      if (wasPrimary) {
        const newPrimary = remaining[0] || null;
        await updateTask(taskId, { assigned: newPrimary } as Record<string, unknown>);
        if (newPrimary) {
          await taskAssignmentService.setPrimary(taskId, newPrimary);
        }
      }

      // Update local state
      setTaskAssignmentsMap(prev => {
        if (remaining.length === 0) {
          const next = { ...prev };
          delete next[taskId];
          return next;
        }
        return { ...prev, [taskId]: remaining };
      });
    } catch (err) {
      console.error('[ProjectContext] Erro ao remover assignee:', err);
    }
  };

  const setTaskPrimaryAssignee = async (taskId: string, memberId: string): Promise<void> => {
    if (!currentProjectId) return;
    try {
      await taskAssignmentService.setPrimary(taskId, memberId);
      await updateTask(taskId, { assigned: memberId } as Record<string, unknown>);

      // Update local state — move to front
      setTaskAssignmentsMap(prev => {
        const current = prev[taskId] || [];
        const withoutMember = current.filter(id => id !== memberId);
        return { ...prev, [taskId]: [memberId, ...withoutMember] };
      });
    } catch (err) {
      console.error('[ProjectContext] Erro ao definir primary assignee:', err);
    }
  };

  /**
   * Atualiza atomicamente a lista completa de assignees de uma tarefa.
   * Calcula diff internamente — sem race condition.
   */
  const setAssignees = async (taskId: string, memberIds: string[]): Promise<void> => {
    if (!currentProjectId) return;
    try {
      const current = taskAssignmentsMap[taskId] || [];
      const toAdd = memberIds.filter(id => !current.includes(id));
      const toRemove = current.filter(id => !memberIds.includes(id));
      const newPrimary = memberIds[0] || null;

      // 1. Atualizar local state PRIMEIRO (evita stale reads)
      setTaskAssignmentsMap(prev => {
        if (memberIds.length === 0) {
          const next = { ...prev };
          delete next[taskId];
          return next;
        }
        return { ...prev, [taskId]: memberIds };
      });

      // 2. Atualizar primary (legacy assigned field)
      const flatList = flattenTasks(tasks);
      const task = flatList.find(t => t.id === taskId);
      const currentPrimary = task?.assigned || null;
      if (newPrimary !== currentPrimary) {
        updateTask(taskId, { assigned: newPrimary } as Record<string, unknown>);
      }

      // 3. Sync junction table
      const ops: Promise<unknown>[] = [];
      for (const id of toAdd) {
        const isPrimary = id === memberIds[0] && !current.includes(memberIds[0]);
        ops.push(taskAssignmentService.addAssignee(taskId, id, isPrimary));
      }
      for (const id of toRemove) {
        ops.push(taskAssignmentService.removeAssignee(taskId, id));
      }
      await Promise.all(ops);

      // 4. Se mudou o primary, sync no DB
      if (newPrimary && newPrimary !== currentPrimary && !toAdd.includes(newPrimary)) {
        await taskAssignmentService.setPrimary(taskId, newPrimary);
      }

      // Notifications handled by DB trigger (trg_notify_task_assigned)
    } catch (err) {
      console.error('[ProjectContext] Erro ao definir assignees:', err);
    }
  };

  // ========= Sprint operations =========

  const addSprint = async (data: Partial<Sprint>): Promise<Sprint | null> => {
    if (!currentProjectId) return null;
    try {
      const created = await sprintService.create({
        ...data,
        project_id: currentProjectId,
        name: data.name || 'Novo Sprint',
        position: sprints.length
      });
      setSprints(prev => [...prev, created]);
      return created;
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Erro ao criar sprint:', err);
      setError(e.message);
      return null;
    }
  };

  const updateSprint = async (id: string, updates: Partial<Sprint>): Promise<void> => {
    try {
      const updated = await sprintService.update(id, updates);
      setSprints(prev => prev.map(s => s.id === id ? updated : s));
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Erro ao atualizar sprint:', err);
      setError(e.message);
    }
  };

  const deleteSprint = async (id: string): Promise<void> => {
    try {
      await sprintService.delete(id);
      setSprints(prev => prev.filter(s => s.id !== id));
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Erro ao deletar sprint:', err);
      setError(e.message);
    }
  };

  const handleStartSprint = async (id: string): Promise<void> => {
    if (!currentProjectId) return;
    try {
      const activated = await sprintService.startSprint(id, currentProjectId);
      setSprints(prev => prev.map(s => {
        if (s.id === id) return activated;
        if (s.status === 'active') return { ...s, status: 'completed' as const };
        return s;
      }));
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Erro ao iniciar sprint:', err);
      setError(e.message);
    }
  };

  const handleCompleteSprint = async (id: string): Promise<void> => {
    try {
      const completed = await sprintService.completeSprint(id);
      setSprints(prev => prev.map(s => s.id === id ? completed : s));
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Erro ao concluir sprint:', err);
      setError(e.message);
    }
  };

  // Baseline operations
  const saveBaseline = useCallback(async (): Promise<void> => {
    if (!project) return;
    try {
      await taskService.saveBaseline(project.id);
      // Recarregar tasks para refletir dados de baseline
      const freshTasks = await taskService.getByProject(project.id);
      setTasks(buildTaskTree(freshTasks));
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Erro ao salvar baseline:', err);
      setError(e.message);
    }
  }, [project]);

  const clearBaseline = useCallback(async (): Promise<void> => {
    if (!project) return;
    try {
      await taskService.clearBaseline(project.id);
      const freshTasks = await taskService.getByProject(project.id);
      setTasks(buildTaskTree(freshTasks));
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Erro ao limpar baseline:', err);
      setError(e.message);
    }
  }, [project]);

  // Verificar se o projeto tem baseline salvo
  const hasBaseline = useCallback((): boolean => {
    const flat = flattenTasks(tasks);
    return flat.some((t: Task) => t.baseline_start_date != null);
  }, [tasks]);

  // Selection - suporte a multi-select com Ctrl e Shift
  const selectTask = useCallback((taskId: string, options: { ctrlKey?: boolean; shiftKey?: boolean } = {}): void => {
    const { ctrlKey = false, shiftKey = false } = options;

    if (ctrlKey) {
      // Ctrl+Click: toggle selection
      setSelectedTaskIds(prev => {
        if (prev.includes(taskId)) {
          return prev.filter(id => id !== taskId);
        } else {
          return [...prev, taskId];
        }
      });
      setSelectedTaskId(taskId);
    } else if (shiftKey && selectedTaskId) {
      // Shift+Click: range selection
      const flatList = flattenTasks(tasks);
      const currentIndex = flatList.findIndex((t: Task) => t.id === selectedTaskId);
      const targetIndex = flatList.findIndex((t: Task) => t.id === taskId);

      if (currentIndex !== -1 && targetIndex !== -1) {
        const start = Math.min(currentIndex, targetIndex);
        const end = Math.max(currentIndex, targetIndex);
        const rangeIds = flatList.slice(start, end + 1).map((t: Task) => t.id);
        setSelectedTaskIds(rangeIds);
      }
    } else {
      // Click normal: single selection
      setSelectedTaskId(taskId);
      setSelectedTaskIds([taskId]);
    }
  }, [selectedTaskId, tasks]);

  /**
   * Leva o usuário até uma tarefa específica, possivelmente de outro projeto.
   *
   * Existe por causa da notificação: `NotificationDropdown` sempre entregou
   * `(project_id, task_id)` e o consumidor na Sidebar descartava o segundo,
   * abrindo o Kanban do projeto certo sem nada destacado. Em quadro com dezenas
   * de cartões, isso transforma o aviso em caça ao tesouro.
   *
   * É intenção PENDENTE, não ação imediata: `switchProject` dispara carga
   * assíncrona, então a tarefa ainda não existe em `tasks` no momento do
   * clique. O efeito abaixo aplica assim que ela aparece.
   */
  const focusTask = useCallback((projectId: string, taskId: string): void => {
    if (projectId && projectId !== project?.id) switchProject(projectId);
    setPendingFocusTaskId(taskId);
  }, [project?.id, switchProject]);

  useEffect(() => {
    if (!pendingFocusTaskId) return;
    if (!flattenTasks(tasks).some((t: Task) => t.id === pendingFocusTaskId)) return;

    const taskId = pendingFocusTaskId;
    setPendingFocusTaskId(null);
    selectTask(taskId);

    // O nó pode ainda não estar no DOM (render do módulo) ou estar dentro de um
    // pai recolhido, caso em que ele nunca aparece. Algumas tentativas curtas e
    // desiste: a seleção já foi aplicada, então ao expandir o pai a tarefa
    // aparece destacada de qualquer forma.
    let tentativas = 0;
    const procurar = () => {
      const el = document.querySelector(`[data-task-id="${taskId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (++tentativas < 10) setTimeout(procurar, 120);
    };
    procurar();
  }, [pendingFocusTaskId, tasks, selectTask]);

  // Limpar seleção múltipla
  const clearSelection = useCallback((): void => {
    setSelectedTaskId(null);
    setSelectedTaskIds([]);
  }, []);

  // Verificar se uma tarefa está selecionada
  const isTaskSelected = useCallback((taskId: string): boolean => {
    return selectedTaskIds.includes(taskId);
  }, [selectedTaskIds]);

  // Operações em lote - Deletar tarefas selecionadas
  const deleteSelectedTasks = useCallback(async (): Promise<void> => {
    if (selectedTaskIds.length === 0) return;

    const count = selectedTaskIds.length;
    if (!confirm(`Tem certeza que deseja excluir ${count} tarefa(s)?`)) return;

    // Coalescer as N exclusões numa única ação de undo
    beginUndoCapture();
    for (const taskIdItem of selectedTaskIds) {
      await deleteTask(taskIdItem);
    }
    commitUndoCapture(`Excluir ${count} tarefa(s)`);

    clearSelection();
  }, [selectedTaskIds, deleteTask, clearSelection, beginUndoCapture, commitUndoCapture]);

  // Operações em lote - Mover tarefas selecionadas para cima
  const moveSelectedTasksUp = useCallback((): void => {
    if (selectedTaskIds.length === 0) return;

    // Mover cada tarefa na ordem (de cima para baixo)
    const flatList = flattenTasks(tasks);
    const sortedIds = [...selectedTaskIds].sort((a, b) => {
      const indexA = flatList.findIndex((t: Task) => t.id === a);
      const indexB = flatList.findIndex((t: Task) => t.id === b);
      return indexA - indexB;
    });

    beginUndoCapture();
    sortedIds.forEach(id => {
      moveTaskUpHandler(id);
    });
    commitUndoCapture('Mover tarefas');
  }, [selectedTaskIds, tasks, moveTaskUpHandler, beginUndoCapture, commitUndoCapture]);

  // Operações em lote - Mover tarefas selecionadas para baixo
  const moveSelectedTasksDown = useCallback((): void => {
    if (selectedTaskIds.length === 0) return;

    // Mover cada tarefa na ordem inversa (de baixo para cima)
    const flatList = flattenTasks(tasks);
    const sortedIds = [...selectedTaskIds].sort((a, b) => {
      const indexA = flatList.findIndex((t: Task) => t.id === a);
      const indexB = flatList.findIndex((t: Task) => t.id === b);
      return indexB - indexA; // Ordem inversa
    });

    beginUndoCapture();
    sortedIds.forEach(id => {
      moveTaskDownHandler(id);
    });
    commitUndoCapture('Mover tarefas');
  }, [selectedTaskIds, tasks, moveTaskDownHandler, beginUndoCapture, commitUndoCapture]);

  // Operações em lote - Aumentar recuo das tarefas selecionadas
  const indentSelectedTasks = useCallback((): void => {
    if (selectedTaskIds.length === 0) return;

    const undoBefore = captureBefore();

    setTasks(prev => {
      // Processar na ordem visual (de cima para baixo)
      const flatList = flattenTasks(prev);
      const sortedIds = [...selectedTaskIds].sort((a, b) => {
        const indexA = flatList.findIndex((t: Task) => t.id === a);
        const indexB = flatList.findIndex((t: Task) => t.id === b);
        return indexA - indexB;
      });

      // Guardar pais antigos antes de mover
      const oldParentIds = new Set<string>();
      sortedIds.forEach(id => {
        const task = flatList.find((t: Task) => t.id === id);
        if (task?.parentId) {
          oldParentIds.add(task.parentId);
        }
      });

      // Aplicar indent para cada tarefa sequencialmente numa única atualização
      let newTasks = prev;
      const newParentIds = new Set<string>();

      for (const id of sortedIds) {
        const result = indentTask(newTasks, id);
        if (result) {
          newTasks = result;
          // Encontrar o novo parentId para atualizar no banco
          const updatedFlatList = flattenTasks(newTasks);
          const task = updatedFlatList.find((t: Task) => t.id === id);
          if (task) {
            taskService.update(id, { parent_id: task.parentId || null }).catch(console.error);
            if (task.parentId) {
              newParentIds.add(task.parentId);
            }
            // Validar e limpar predecessoras inválidas
            const cleanResult = cleanInvalidPredecessors(id, updatedFlatList);
            if (cleanResult) {
              taskService.update(id, { dependencies: cleanResult.cleanedDependencies }).catch(console.error);
              newTasks = updateTaskInTree(newTasks, id, { dependencies: cleanResult.cleanedDependencies });
            }

            // Se a tarefa tem um novo pai, verificar se o pai virou épico e limpar suas predecessoras
            // Épicos não podem ter predecessoras - suas datas são calculadas pelas filhas
            if (task.parentId) {
              const newParent = updatedFlatList.find((t: Task) => t.id === task.parentId);
              if (newParent && newParent.dependencies && newParent.dependencies.length > 0) {
                // O pai agora é um épico (tem filhas), deve perder suas predecessoras
                newTasks = updateTaskInTree(newTasks, task.parentId, { dependencies: [] });
                taskService.update(task.parentId, { dependencies: [] }).catch(console.error);
              }
            }
          }
        }
      }

      // Propagar progresso e datas para todos os pais afetados
      const settings = getProjectDateSettings(project as Project);
      const allAffectedParentIds = new Set([...oldParentIds, ...newParentIds]);

      allAffectedParentIds.forEach(parentId => {
        const recalcFlatList = flattenTasks(newTasks);
        const parentChildren = recalcFlatList.filter((t: Task) => t.parentId === parentId);
        if (parentChildren.length > 0) {
          const parentPropUpdates = propagateToParents(recalcFlatList, parentChildren[0].id, settings);
          parentPropUpdates.forEach((update) => {
            newTasks = updateTaskInTree(newTasks, update.taskId, {
              startDate: update.startDate!,
              endDate: update.endDate!,
              duration: update.duration,
              progress: update.progress
            });
            taskService.update(update.taskId, {
              startDate: update.startDate!,
              endDate: update.endDate!,
              duration: update.duration,
              progress: update.progress
            }).catch(console.error);
          });
        }
      });

      return newTasks;
    });

    maybePushUndo('Indentar tarefas', undoBefore);
  }, [selectedTaskIds, project, captureBefore, maybePushUndo]);

  // Operações em lote - Reduzir recuo das tarefas selecionadas
  const outdentSelectedTasks = useCallback((): void => {
    if (selectedTaskIds.length === 0) return;

    const undoBefore = captureBefore();

    setTasks(prev => {
      // Processar na ordem visual (de cima para baixo)
      const flatList = flattenTasks(prev);
      const sortedIds = [...selectedTaskIds].sort((a, b) => {
        const indexA = flatList.findIndex((t: Task) => t.id === a);
        const indexB = flatList.findIndex((t: Task) => t.id === b);
        return indexA - indexB;
      });

      // Guardar pais antigos antes de mover
      const oldParentIds = new Set<string>();
      sortedIds.forEach(id => {
        const task = flatList.find((t: Task) => t.id === id);
        if (task?.parentId) {
          oldParentIds.add(task.parentId);
        }
      });

      // Aplicar outdent para cada tarefa sequencialmente numa única atualização
      let newTasks = prev;
      const newParentIds = new Set<string>();

      for (const id of sortedIds) {
        const result = outdentTask(newTasks, id);
        if (result) {
          newTasks = result;
          // Encontrar o novo parentId para atualizar no banco
          const updatedFlatList = flattenTasks(newTasks);
          const task = updatedFlatList.find((t: Task) => t.id === id);
          if (task) {
            taskService.update(id, { parent_id: task.parentId || null }).catch(console.error);
            if (task.parentId) {
              newParentIds.add(task.parentId);
            }
            // Validar e limpar predecessoras inválidas
            const cleanResult = cleanInvalidPredecessors(id, updatedFlatList);
            if (cleanResult) {
              taskService.update(id, { dependencies: cleanResult.cleanedDependencies }).catch(console.error);
              newTasks = updateTaskInTree(newTasks, id, { dependencies: cleanResult.cleanedDependencies });
            }
          }
        }
      }

      // Propagar progresso e datas para todos os pais afetados
      const settings = getProjectDateSettings(project as Project);
      const allAffectedParentIds = new Set([...oldParentIds, ...newParentIds]);

      allAffectedParentIds.forEach(parentId => {
        const recalcFlatList = flattenTasks(newTasks);
        const parentChildren = recalcFlatList.filter((t: Task) => t.parentId === parentId);
        if (parentChildren.length > 0) {
          const parentPropUpdates = propagateToParents(recalcFlatList, parentChildren[0].id, settings);
          parentPropUpdates.forEach((update) => {
            newTasks = updateTaskInTree(newTasks, update.taskId, {
              startDate: update.startDate!,
              endDate: update.endDate!,
              duration: update.duration,
              progress: update.progress
            });
            taskService.update(update.taskId, {
              startDate: update.startDate!,
              endDate: update.endDate!,
              duration: update.duration,
              progress: update.progress
            }).catch(console.error);
          });
        }
      });

      return newTasks;
    });

    maybePushUndo('Reduzir recuo das tarefas', undoBefore);
  }, [selectedTaskIds, project, captureBefore, maybePushUndo]);

  // Operações em lote - Encadear predecessoras das tarefas selecionadas
  const linkSelectedTasks = useCallback(async (): Promise<void> => {
    if (selectedTaskIds.length < 2) return; // Precisa de pelo menos 2 tarefas

    // Ordenar tarefas pela ordem visual e filtrar épicos
    const flatList = flattenTasks(tasks);
    const sortedIds = [...selectedTaskIds]
      .sort((a, b) => {
        const indexA = flatList.findIndex((t: Task) => t.id === a);
        const indexB = flatList.findIndex((t: Task) => t.id === b);
        return indexA - indexB;
      })
      .filter(id => {
        // Excluir épicos (tarefas com filhos) do encadeamento
        const task = flatList.find((t: Task) => t.id === id);
        return task && !task.hasChildren;
      });

    if (sortedIds.length < 2) return; // Precisa de pelo menos 2 tarefas não-épicas

    const settings = getProjectDateSettings(project as Project);

    // Encadear: cada tarefa (exceto a primeira) depende da anterior
    setTasks(prev => {
      let newTasks = prev;

      for (let i = 1; i < sortedIds.length; i++) {
        const currentTaskId = sortedIds[i];
        const predecessorId = sortedIds[i - 1];

        // Atualizar a flat list após cada iteração para pegar as datas atualizadas
        const currentFlatList = flattenTasks(newTasks);
        const currentTask = currentFlatList.find((t: Task) => t.id === currentTaskId);
        const predecessorTask = currentFlatList.find((t: Task) => t.id === predecessorId);

        if (currentTask && predecessorTask) {
          // Adicionar predecessora (sem duplicar se já existir)
          const existingDeps = currentTask.dependencies || [];
          const newDependencies = existingDeps.includes(predecessorId)
            ? existingDeps
            : [...existingDeps, predecessorId];

          // Calcular nova data de início: próximo dia útil após o término da predecessora
          let newStartDate = currentTask.startDate;
          if (predecessorTask.endDate) {
            // Adiciona 1 dia e garante que seja dia útil
            const dayAfterPredecessor = addDays(new Date(predecessorTask.endDate), 1);
            newStartDate = getNextWorkDay(dayAfterPredecessor, settings.workDays, settings.holidays);
          }

          // Processar a atualização completa (recalcula endDate baseado na duração)
          const { taskUpdates, cascadeUpdates } = processTaskUpdate(
            currentTask,
            { startDate: newStartDate ?? undefined, dependencies: newDependencies },
            currentFlatList,
            settings
          );

          // Atualizar localmente
          newTasks = updateTaskInTree(newTasks, currentTaskId, {
            ...taskUpdates,
            dependencies: newDependencies
          } as Partial<Task>);

          // Salvar no banco
          taskService.update(currentTaskId, {
            dependencies: newDependencies,
            startDate: taskUpdates.startDate,
            endDate: taskUpdates.endDate,
            duration: taskUpdates.duration
          }).catch(console.error);

          // Aplicar cascata para tarefas dependentes
          cascadeUpdates.forEach((update) => {
            newTasks = updateTaskInTree(newTasks, update.taskId, update as Partial<Task>);
            taskService.update(update.taskId, {
              startDate: update.startDate!,
              endDate: update.endDate!,
              duration: update.duration
            }).catch(console.error);
          });

          // Propagar alterações para os pais (épicos)
          const updatedFlatList = flattenTasks(newTasks);
          const parentUpdates = propagateToParents(updatedFlatList, currentTaskId, settings);
          parentUpdates.forEach((update) => {
            newTasks = updateTaskInTree(newTasks, update.taskId, {
              startDate: update.startDate!,
              endDate: update.endDate!,
              duration: update.duration,
              progress: update.progress
            });
            taskService.update(update.taskId, {
              startDate: update.startDate!,
              endDate: update.endDate!,
              duration: update.duration,
              progress: update.progress
            }).catch(console.error);
          });
        }
      }

      return newTasks;
    });
  }, [selectedTaskIds, tasks, project]);

  // Operações em lote - Destacar (pintar) as tarefas selecionadas
  const highlightSelectedTasks = useCallback(async (color: string | null): Promise<void> => {
    if (selectedTaskIds.length === 0) return;

    // Coalescer as N pinturas numa única ação de undo
    beginUndoCapture();
    for (const taskIdItem of selectedTaskIds) {
      await updateTask(taskIdItem, { color });
    }
    commitUndoCapture(`Destacar ${selectedTaskIds.length} tarefa(s)`);

    // A linha selecionada é pintada de azul por cima do destaque (o !important
    // do Tailwind vence o style inline). Sem limpar a seleção, o usuário não vê
    // a cor que acabou de escolher.
    clearSelection();
  }, [selectedTaskIds, updateTask, clearSelection, beginUndoCapture, commitUndoCapture]);


  // Helper function
  const findTask = (taskList: Task[], taskId: string): Task | null => {
    for (const task of taskList) {
      if (task.id === taskId) return task;
      if (task.children) {
        const found = findTask(task.children, taskId);
        if (found) return found;
      }
    }
    return null;
  };

  const getSelectedTask = useCallback((): Task | null => {
    return selectedTaskId ? findTask(tasks, selectedTaskId) : null;
  }, [selectedTaskId, tasks]);

  // Função para obter estatísticas globais de todos os projetos (para o HomeModule)
  const getAllProjectsStats = useCallback((): GlobalProjectStats => {
    const now = new Date();
    const sevenDaysLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    // Helper para flatten tasks
    const flattenTaskList = (taskList: Task[]): Task[] => {
      let result: Task[] = [];
      taskList.forEach(task => {
        if (task.type === 'task' || !task.type) {
          result.push(task);
        }
        if (task.children && task.children.length > 0) {
          result = result.concat(flattenTaskList(task.children));
        }
      });
      return result;
    };

    // Se só temos o projeto atual carregado, usar dados dele
    const flatTasksList = flattenTaskList(tasks);

    const totalTasks = flatTasksList.length;
    const completedTasks = flatTasksList.filter(t => (t.progress || 0) >= 100).length;
    const overdueTasks = flatTasksList.filter(t => {
      const endDate = new Date(t.endDate as Date);
      return endDate < now && (t.progress || 0) < 100;
    }).length;

    // Tarefas próximas do vencimento (próximos 7 dias)
    const upcomingTasks = flatTasksList
      .filter(t => {
        const endDate = new Date(t.endDate as Date);
        return endDate >= now && endDate <= sevenDaysLater && (t.progress || 0) < 100;
      })
      .map(t => {
        const endDate = new Date(t.endDate as Date);
        const diffDays = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        let dueLabel: string;
        const tp = getCurrentCopy().common.project;
        if (diffDays === 0) dueLabel = tp.today;
        else if (diffDays === 1) dueLabel = tp.tomorrow;
        else dueLabel = fmt.date(endDate, { day: '2-digit', month: '2-digit' });

        return {
          id: t.id,
          name: t.name,
          projectId: project?.id || null,
          projectName: project?.name || '',
          dueLabel,
          isOverdue: false
        };
      })
      .slice(0, 10);

    // Estatísticas por projeto
    const projectStats: Record<string, { total: number; completed: number; overdue: number }> = {};
    if (project) {
      projectStats[project.id] = {
        total: totalTasks,
        completed: completedTasks,
        overdue: overdueTasks
      };
    }

    return {
      totalProjects: projects.length,
      totalTasks,
      completedTasks,
      overdueTasks,
      projectStats,
      upcomingTasks
    };
  }, [tasks, projects, project]);

  /**
   * Grava o quadro inteiro e recarrega o que o banco devolveu.
   *
   * Recarregar, e não confiar no otimista: o serviço normaliza `position` e
   * devolve `name` nulo para coluna canônica não renomeada, e é esse resultado
   * que precisa valer na tela. Confiar no otimista faria a coluna canônica
   * aparecer com o texto congelado até o próximo reload.
   */
  const saveKanbanColumns = useCallback(async (novasColunas: KanbanColumn[]) => {
    if (!project?.id) return;
    const salvas = await kanbanColumnService.saveAll(project.id, novasColunas);
    setKanbanColumns(salvas.length > 0 ? salvas : DEFAULT_KANBAN_COLUMNS);
  }, [project?.id]);

  /**
   * Reatribui em massa os cartões de uma coluna que vai deixar de existir.
   *
   * Atualiza o estado local no mesmo gesto: sem isso os cartões continuariam
   * desenhados numa coluna que o editor acabou de apagar, e cairiam no
   * fallback da primeira coluna até alguém recarregar a página.
   */
  const moveCardsBetweenColumns = useCallback(async (deColuna: string, paraColuna: string) => {
    if (!project?.id) return;
    await kanbanColumnService.moverCartoes(project.id, deColuna, paraColuna);
    setTasks((prev) => {
      const andar = (lista: Task[]): Task[] => lista.map((t) => ({
        ...t,
        status: t.status === deColuna ? paraColuna : t.status,
        children: t.children ? andar(t.children) : t.children,
      }));
      return andar(prev);
    });
  }, [project?.id]);

  const value: ProjectContextValue = {
    // Multi-project state
    projects,
    currentProjectId,

    // Current project state
    project,
    tasks,
    team,
    sprints,
    kanbanColumns,
    saveKanbanColumns,
    moveCardsBetweenColumns,
    selectedTaskId,

    // Loading states
    loading,
    saving,
    error,

    // Project operations
    createProject,
    switchProject,
    registerCreatedProject,
    aplicarProjetoAtualizado,
    deleteProject,
    renameProject,
    duplicateProject,
    toggleProjectFavorite,
    moveProjectToFolder,
    importProject,
    updateProjectSettings,
    loadProjects: () => loadProjects(user?.id || ''),
    loadProject: loadProjectData,

    // Task operations
    updateTask,
    deleteTask,
    addTask,
    addTaskBelow,
    addMultipleTasks,
    toggleExpand,
    duplicateTask: duplicateTaskItem,
    reorderTasks,
    moveTaskUp: moveTaskUpHandler,
    moveTaskDown: moveTaskDownHandler,
    indentTask: indentTaskHandler,
    outdentTask: outdentTaskHandler,

    // Undo/Redo: agrupamento de mutações (usado pelo arraste no Gantt)
    beginUndoCapture,
    commitUndoCapture,

    // Team operations
    addTeamMember,
    updateTeamMember,
    deleteTeamMember,
    refreshTeam,

    // Multi-assignee operations
    taskAssignmentsMap,
    getTaskAssignees,
    addAssignee,
    removeAssignee,
    setTaskPrimaryAssignee,
    setAssignees,

    // Sprint operations
    addSprint,
    updateSprint,
    deleteSprint,
    startSprint: handleStartSprint,
    completeSprint: handleCompleteSprint,

    // Column operations
    columns,
    addColumn,
    updateColumn,
    deleteColumn,
    reorderColumns,
    toggleColumnVisibility,
    resizeColumn,
    resetColumnsToDefault,
    getVisibleColumns,

    // Selection
    selectTask,
    focusTask,
    getSelectedTask,
    selectedTaskIds,
    isTaskSelected,
    clearSelection,
    deleteSelectedTasks,
    moveSelectedTasksUp,
    moveSelectedTasksDown,
    indentSelectedTasks,
    outdentSelectedTasks,
    linkSelectedTasks,
    highlightSelectedTasks,

    // Baseline operations
    saveBaseline,
    clearBaseline,
    hasBaseline,

    // Stats for Home
    getAllProjectsStats
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};
